import { memo, useEffect, useState, useMemo, useRef, useCallback } from "react"
import { createPortal } from "react-dom"
import type { MouseEvent as ReactMouseEvent } from "react"
import { Button } from "@/components/ui/button"
import { testIds } from "@/lib/test-ids"
import { findLiteralSkillKeywordSpan } from "@/lib/skill-keyword-span"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger } from "@/components/ui/select"
import { ChevronDown, ChevronRight, X, Brain, Zap, Loader2, ArrowDown, Pencil } from "lucide-react"
import { useDroppable } from "@dnd-kit/core"
import { ConversationHeader } from "@/components/features/conversation-header"
import { ConversationHistoryPanel } from "@/components/features/conversation-history-panel"
import { ConversationRenameDialog } from "@/components/features/conversation-rename-dialog"
import {
  FlowAgentOriginBar,
  useFlowAgentOrigin,
} from "@/components/features/chats/flow-agent-origin-bar"
import { useWorkspace } from "@/providers/workspace-provider"
import { useAuth } from "@/providers/auth-provider"
import { DiffPreviewDialog } from "./diff-preview-dialog"
import { DocumentEditReview } from "@/components/features/document-edit-review"
import { ChatFirstHome } from '@/components/layout/chat-first-home'
import {
  ChatContextMeter,
  type ChatContextUsage,
} from '@/components/layout/chat-context-meter'
import { useActiveDocumentContextStore, type ActiveDocumentContextStore } from './chat-pane.active-document-context'
import { ChatPaneComposerContextSlot } from './chat-pane.composer-context'
import { ChatPaneCollapsedState, ChatPaneDropZoneOverlay } from './chat-pane.presentational'
import { getActiveConversations, getConversation, autoNameConversation, fetchWithAuth, stageChatImagesForSend } from "@/lib/api"
import { patchChatMessageMetadata } from "@/lib/chat-message-metadata"
import { isAbortFetchError } from "@/lib/api/core"
import { getThread, deleteThread } from '@/lib/api/threads'
import { ContentEditableChatInput } from "@/components/ui/contenteditable-chat-input"
import { referencesToInlineMarkers, referenceListedWithSameKey } from "@/components/ui/contenteditable-chat-input.utils"
import { filterOpenTabIdsForSearch } from "@/lib/open-tab-ids-for-search"
import { prefetchDocumentSearchContext } from "@/hooks/use-document-search-boost-ids"
import type { SlashSkillRow } from "@/components/ui/slash-command-menu"
import { cn } from "@/lib/utils"
import {
  fullChatModelDisplayName,
  resolveChatModelId,
  shortChatModelDisplayName,
} from "@/lib/chat-model-display"
import { toast } from 'sonner'
import {
  chatToolbarActionEventName,
  type ChatToolbarAction,
} from '@/lib/chat-toolbar-events'
import {
  chatFeedbackOpenEventName,
  type ChatFeedbackCategory,
  type ChatFeedbackOpenDetail,
} from '@/lib/chat-feedback'
import { FeedbackModal } from '@/components/features/chats/FeedbackModal'
import { documentUrlForPathOrId } from '@/lib/app-routes'
import {
  draftScopeDiffersFromActive,
  organizationIdForChatImageUpload,
  showApplyScopeToAppToast,
  workspaceScopePathsForPersist,
} from '@/lib/chat-conversation-scope'
import { ThinkingModePanel } from "@/components/thinking-mode/thinking-mode-panel"
import { ToolMessage } from '@/components/features/chat-messages/tool-message'
import { ThinkingTimeline } from '@/components/features/chat-messages/thinking-timeline'
import { isFirstClassToolWidget } from '@/components/features/chat-messages/thinking-timeline.helpers'
import { AssistantMessage } from '@/components/features/chat-messages/assistant-message'
import { FlashCardRunProvider, useFlashCardRun } from '@/components/features/chat-messages/flash-card-run-context'
import { IngestionRunCard, buildRunHeaderLabel } from '@/components/features/chat-messages/ingestion-progress-message'
import { UserMessage, hasFlowNoteInlineMarkers, UserPromptMarkdownBody } from '@/components/features/chat-messages/user-message'
import { ChatPostedImages } from '@/components/features/chat-messages/chat-posted-images'
import { isUpdateWorkSpecSeedPrompt } from '@/lib/update-work-spec-prompt-display'
import { MessageReferences } from '@/components/features/chat-messages/message-common'
import { CollapsibleModifiedFilesPanel } from '@/components/features/collapsible-modified-files-panel'
import { BasecampNotesDraftPanel } from '@/components/integrations/basecamp/components/basecamp-notes-draft-panel'
import type { BasecampNotesDraft } from '@/components/integrations/basecamp/components/basecamp-notes-draft-panel'
import { BasecampCreateTodoDraftPanel } from '@/components/integrations/basecamp/components/basecamp-create-todo-draft-panel'
import type { BasecampCreateTodoDraft } from '@/lib/basecamp-create-todo-draft'
import { markBasecampCreateDraftConsumed } from '@/lib/basecamp-create-todo-draft'
import type { ChatPaneProps, Message, DocumentReference, ProposedEditData, ChatImage, QueuedChatMessage, ViewContext } from './chat-pane.types'
import {
  applyQueueTextUpdate,
  captureComposerQueueItem,
  closeQueueEditSession,
  openQueueEditSession,
  queuedMessagePreview,
  removeQueuedById,
  resolveStagedImagesForSend,
  type QueueEditSession,
} from '@/lib/chat-composer-queue'
import { decideQueueFlush } from './chat-pane.queue-flush'
import { useChatPaneState } from './chat-pane.hooks'
import { useLLMProvider, useDragAndDrop, useReferenceSync, useConversationPolling } from './chat-pane.hooks'
import { useChatHandlers, useEditHandlers, useConversationHandlers, normalizeChangedLines, type EditHandlerOptions } from './chat-pane.handlers'
import { 
  useConversationHistoryLoader, 
  useWorkspaceScopeApplier, 
  useConversationIdPersistence,
  useOpenConversationListener,
  useChatEventListeners,
  useScrollDetection,
  useStreamFollowScroll,
  useBasecampNotesListeners,
  useTriageAnalyzeListener,
  useTodoActionChatListener,
} from './chat-pane.custom-hooks'

import { devLog } from "@/lib/dev-log"
import {
  clearDismissedDocIdsForConversation,
  loadDismissedDocIds,
  persistDismissedDocIds,
} from '@/lib/ai-context-dismissals'
import { executeGeneralChatStream, buildChatStreamRequestBody } from './chat-pane.streaming'
import { SessionFilesStrip } from './session-files-strip'
import {
  chatContextFromOpenDocument,
  isSessionScratchDisplayTitle,
  isSessionScratchPath,
  visibleMessageReferences,
} from '@/lib/session-scratch-path'
import {
  CHAT_IMAGE_BUDGET_TOAST_TITLE,
  CHAT_PAYLOAD_TOAST_TITLE,
  validateChatStreamPayload,
  validateStagedImagesOnly,
} from '@/lib/chat-image-budget'
import { ChatModeSelector, type ChatMode } from './chat-mode-selector'
import { StorageKeys } from '@/lib/storage/keys'

import {
  chatContentRootFromRefPath,
  chatScopeRootFromDocumentPath,
  collectIngestionAssistantGroup,
  findIngestionTerminalIndex,
  formatChatScopeWidenLabels,
  hasLaterAssistant,
  isPlanBuildUserText,
  messagesLookLikeIngestionRun,
  PLAN_BUILD_USER_TEXT,
  shouldGroupAssistantMessagesAsIngestionRun,
  shouldShowAssistantTimestamp,
  shouldKeepLiveStreamOnHostedThreadSwitch,
  shouldShowChatFirstHome,
  shouldSkipHostedHistoryReload,
  rememberHeldUserTurn,
  peekHeldUserTurn,
  clearHeldUserTurn,
  scrollLatestUserTurnToTop,
  buildAssistantTurnCopyByIndex,
  messageForAssistantDisplay,
} from "./chat-pane.utils"

import {
  BASECAMP_INGESTION_COMPLETE,
  dispatchBasecampTodoRefreshFromNotesWrite,
  type BasecampIngestionCompleteDetail,
} from '@/lib/basecamp-ingestion-events'
import { persistBasecampNotesOutcome } from '@/lib/basecamp-notes-draft'
import { flipIngestionConversationToStreaming } from '@/lib/ingestion-streaming-flip'
import { useChatSurfaceRegistryOptional } from '@/hooks/use-chat-surface-registry'
import { refreshChatsSidebar, dismissChatThreadSurfaces, focusChatComposerEventName } from '@/lib/chats-sidebar-events'
import {
  consumeUpdateWorkSpecKickoffIfReady,
  enqueueUpdateWorkSpecKickoff,
  UPDATE_WORK_SPEC_KICKOFF_EVENT,
} from '@/lib/update-work-spec-kickoff'
import {
  CHAT_COMPACT_FOCUS_MAX_CLASS,
  CHAT_EDITOR_FOCUS_MAX_CLASS,
  CHAT_PANE_CONTAINER_CLASS,
  chatComposerFooterPadClassName,
  chatComposerGutterClassName,
  chatEditorFocusColumnClassName,
  chatMessageGutterClassName,
  isWideChatSurface,
} from '@/lib/chat-focus-layout'
import {
  chatWorkspaceAccentStyles,
  colorsForWorkspaceIds,
} from '@/lib/chat-workspace-accent'
import type { ChatSessionSnapshot } from '@/lib/chat-session-snapshot'
import type { ThinkingOperation } from './chat-pane.streaming'
import { isPendingProposedEdit, proposedEditMatches } from '@/lib/proposed-edit-utils'
import {
  pendingProposedEditsInMessages,
  readPendingProposedEdits,
  replacePendingProposedEditCards,
  writePendingProposedEdits,
} from '@/lib/pending-proposed-edits'

const pendingReviewSignature = new Map<string, string>()
import { resolveActionChoiceTarget } from '@/lib/chat-action-choice'
import { buildMessageDocumentsMap } from '@/lib/chat-markdown-links'
import {
  extractPlainCommentDraft,
  getActiveTodoActionSession,
} from '@/lib/open-basecamp-todo-action-chat'
import {
  buildChatModifiedFilesPanelRows,
  type ChatModifiedFileRow,
} from '@/lib/chat-modified-files-panel'
import { coalesceCompletedFiles } from '@/lib/chat-completed-files'
import { completedFilesFromMessages } from '@/lib/chat-message-hydration'
import type { ActionChoice } from './chat-pane.types'

export function ChatPane({
  activeDocumentId,
  activeDocumentTitle,
  references = [],
  ...shellProps
}: ChatPaneProps = {}) {
  const activeDocumentContext = useActiveDocumentContextStore(
    activeDocumentId,
    activeDocumentTitle,
    references,
  )

  return (
    <FlashCardRunProvider>
      <ChatPaneShell
        {...shellProps}
        references={references}
        activeDocumentContext={activeDocumentContext}
      />
    </FlashCardRunProvider>
  )
}

type ChatPaneShellProps = Omit<ChatPaneProps, 'activeDocumentId' | 'activeDocumentTitle'> & {
  activeDocumentContext: ActiveDocumentContextStore
}

const ChatPaneShell = memo(function ChatPaneShell({
  hostedThreadId,
  hostedThreadTitle: _hostedThreadTitle,
  hostedLinkedConversationId,
  hostedThreadResolving = false,
  surfaceVariant,
  viewContext,
  references = [],
  onRemoveReference,
  onClearReferences,
  onAddReference,
  collapsed = false,
  onToggleCollapse,
  onDocumentClick,
  onFolderClick,
  openTabIds = [],
  recentlyViewedIds = [],
  activeDocumentPath,
  activeDocumentContext,
}: ChatPaneShellProps) {
  const { user } = useAuth()
  const { pauseOnUserChat } = useFlashCardRun()
  const registry = useChatSurfaceRegistryOptional()
  const registryRef = useRef(registry)
  registryRef.current = registry
  const { activeDocumentIdRef, activeDocumentTitleRef } = activeDocumentContext
  const activeDocumentPathRef = useRef(activeDocumentPath)
  activeDocumentPathRef.current = activeDocumentPath
  const hostedThreadIdRef = useRef(hostedThreadId)
  hostedThreadIdRef.current = hostedThreadId
  const prevHostedThreadIdRef = useRef(hostedThreadId)
  /** Tracks last hostedThreadId seen by the load effect (explicit sidebar switch detection). */
  const prevHostedLoadThreadRef = useRef(hostedThreadId)
  /** Tracks linked conversation id so late hydrate (inbox) force-loads the right chat. */
  const prevHostedLinkedConversationIdRef = useRef(hostedLinkedConversationId)
  /** Thread id user switched to that still needs a forced conversation load (survives resolve re-runs). */
  const pendingHostedLoadThreadRef = useRef<string | null>(null)
  /** Blocks hosted-thread reload while (+) / Cmd+N clears state before registry switches to new-*. */
  const skipHostedThreadLoadRef = useRef(false)
  /** Conversation ID that returned 404/410 on last load attempt - stops the load effect below from
   * retrying a permanently-gone conversation on every dependency-driven re-run (was causing an
   * infinite fetch loop; see 2026-07-06 production incident). Cleared implicitly once
   * hostedLinkedConversationId no longer matches (new/different conversation). */
  const failedHostedLoadConversationIdRef = useRef<string | null>(null)
  // Keep loading/polling/activeConversations out of hosted-load effect deps so their
  // churn cannot re-fire loads (AbortError retry storm / stuck "Loading conversation...").
  const loadingConversationRef = useRef(false)
  const isPollingRef = useRef(false)
  const activeConversationsRef = useRef<Array<{ id: string; metadata?: Record<string, unknown>; title?: string }>>([])

  const chatModeStorageKey = StorageKeys.CHAT_MODE(user?.id)
  const [chatMode, setChatMode] = useState<ChatMode>(() => {
    try {
      const saved = localStorage.getItem(chatModeStorageKey)
      if (saved === 'ask' || saved === 'plan' || saved === 'agent') {
        return saved
      }
    } catch {
      /* ignore */
    }
    return 'agent'
  })

  useEffect(() => {
    try {
      const saved = localStorage.getItem(chatModeStorageKey)
      if (saved === 'ask' || saved === 'plan' || saved === 'agent') {
        setChatMode(saved)
      }
    } catch {
      /* ignore */
    }
  }, [chatModeStorageKey])

  useEffect(() => {
    try {
      localStorage.setItem(chatModeStorageKey, chatMode)
    } catch {
      /* ignore */
    }
  }, [chatMode, chatModeStorageKey])

  /** Sync ref so Build can switch to Agent and send in the same tick. */
  const chatModeRef = useRef<ChatMode>(chatMode)
  useEffect(() => {
    chatModeRef.current = chatMode
  }, [chatMode])

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ mode?: ChatMode; threadId?: string }>).detail
      const mode = detail?.mode
      if (mode !== 'ask' && mode !== 'plan' && mode !== 'agent') return
      if (detail.threadId && hostedThreadId && detail.threadId !== hostedThreadId) return
      chatModeRef.current = mode
      setChatMode(mode)
    }
    window.addEventListener('chat-set-mode', handler)
    return () => window.removeEventListener('chat-set-mode', handler)
  }, [hostedThreadId])

  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameTarget, setRenameTarget] = useState<{ id: string; title: string } | null>(null)

  // Get all state from custom hook
  const state = useChatPaneState()

  // Destructure state for easier access
  const {
    legacyMessages: _legacyMessages,
    setLegacyMessages,
    legacyInput,
    setLegacyInput,
    showHistory,
    setShowHistory,
    historyRefreshKey,
    setHistoryRefreshKey,
    currentConversationId,
    setCurrentConversationId,
    loadingConversation,
    setLoadingConversation,
    isPolling,
    setIsPolling,
    activeConversations,
    setActiveConversations,
    messagesContainerRef,
    pollingIntervalRef,
    deletingConversationsRef,
    streamingMessages,
    setStreamingMessages,
    streamingInput,
    setStreamingInput,
    isStreaming,
    setIsStreaming,
    isTyping,
    setIsTyping,
    copiedCode,
    setCopiedCode: _setCopiedCode,
    streamingConversationId,
    setStreamingConversationId,
    abortControllerRef,
    streamInFlightRef,
    streamEpochRef,
    diffPreviewOpen,
    setDiffPreviewOpen,
    proposedEdit,
    setProposedEdit,
    isApplyingEdit,
    setIsApplyingEdit,
    selectedProvider,
    setSelectedProvider,
    selectedModel,
    setSelectedModel,
    availableModels,
    setAvailableModels,
    providersAvailable,
    setProvidersAvailable,
    allModelsByProvider,
    setAllModelsByProvider,
    userHasScrolled,
    setUserHasScrolled,
    isNearBottom,
    setIsNearBottom,
    contentEditableInputRef,
    prevReferencesRef,
    editingMessageIndex,
    setEditingMessageIndex,
    editContent,
    setEditContent,
    queuedMessages,
    setQueuedMessages,
    thinkingModeActive,
    setThinkingModeActive,
    thinkingOperations,
    setThinkingOperations,
    setCurrentTask,
    completedFiles,
    setCompletedFiles,
    agenticRound,
    setAgenticRound,
    modifiedFilesPanelExpanded,
    setModifiedFilesPanelExpanded,
    messages,
    input,
    isLoading: _isLoading,
  } = state

  /** Sync typing flag for SSE loop (mirrors deprecated closure read on `isTyping`) */
  const isTypingRef = useRef(isTyping)
  useEffect(() => {
    isTypingRef.current = isTyping
  }, [isTyping])

  const streamingMessagesRef = useRef(streamingMessages)
  streamingMessagesRef.current = streamingMessages
  const [turnCommitted, setTurnCommitted] = useState(
    () => Boolean(hostedThreadId && peekHeldUserTurn(hostedThreadId)),
  )
  const committedUserTurnRef = useRef<Message | null>(
    hostedThreadId ? peekHeldUserTurn(hostedThreadId) : null,
  )
  const isStreamingRef = useRef(isStreaming)
  isStreamingRef.current = isStreaming
  const streamingConversationIdRef = useRef(streamingConversationId)
  streamingConversationIdRef.current = streamingConversationId
  const hostedLinkedConversationIdRef = useRef(hostedLinkedConversationId)
  hostedLinkedConversationIdRef.current = hostedLinkedConversationId
  const currentConversationIdRef = useRef(currentConversationId)
  currentConversationIdRef.current = currentConversationId

  loadingConversationRef.current = loadingConversation
  isPollingRef.current = isPolling
  activeConversationsRef.current = activeConversations

  /** Active slash-invoked skill for webapp chat (prompt injection) */
  const [activeChatSkill, setActiveChatSkill] = useState<{ id: string; name: string } | null>(null)
  const [queuePanelExpanded, setQueuePanelExpanded] = useState(true)
  const [queueEdit, setQueueEdit] = useState<QueueEditSession | null>(null)
  const queueEditRef = useRef<QueueEditSession | null>(null)
  const activeSkillIdRef = useRef<string | null>(null)
  const activeSkillRef = useRef<{ id: string; name: string } | null>(null)
  useEffect(() => {
    activeSkillIdRef.current = activeChatSkill?.id ?? null
    activeSkillRef.current = activeChatSkill ?? null
  }, [activeChatSkill])

  /** Pending Basecamp notes draft waiting for user approval before pushing to Basecamp. */
  const [pendingBasecampDraft, setPendingBasecampDraft] = useState<BasecampNotesDraft | null>(null)
  const [pendingBasecampCreateDraft, setPendingBasecampCreateDraft] = useState<BasecampCreateTodoDraft | null>(null)

  /** Latest context window fullness from chat-stream SSE (Track D meter). */
  const [contextUsage, setContextUsage] = useState<ChatContextUsage | null>(null)

  /** Whether the sticky previous-prompt banner is expanded (Cursor-style). */
  const [stickyPromptExpanded, setStickyPromptExpanded] = useState(false)
  const [sendBusy, setSendBusy] = useState(false)
  const sendBusyRef = useRef(false)
  /** User message index to mirror in the sticky banner (most recent one scrolled above viewport). */
  const [stickyUserMsgIndex, setStickyUserMsgIndex] = useState<number | null>(null)

  /** Draft chat scope (composer pip). Persisted to DB on user send; null = follow sidebar active set. */
  const [conversationScope, setConversationScope] = useState<string[] | null>(null)
  const { workspaces, activeWorkspaceIds, setActiveWorkspaceIds, activeFolderPaths: _activeFolderPaths } =
    useWorkspace()

  const resolvedScopeIds = useMemo(
    () => conversationScope ?? activeWorkspaceIds,
    [conversationScope, activeWorkspaceIds],
  )

  const chatScopeAccent = useMemo(() => {
    const colors = colorsForWorkspaceIds(resolvedScopeIds, workspaces)
    return chatWorkspaceAccentStyles(colors)
  }, [resolvedScopeIds, workspaces])

  const composerAccentStyle = useMemo(
    () =>
      chatScopeAccent
        ? { idle: chatScopeAccent.composer, focused: chatScopeAccent.composerFocused }
        : null,
    [chatScopeAccent],
  )

  // Resolve scope to folder paths for API (backend filters by content_root)
  const workspaceScopePathsBase = useMemo(() => {
    if (viewContext?.type === 'basecamp-project' && viewContext.organizationId) {
      const root = workspaces.find((w) => w.id === viewContext.organizationId)?.contentRoot
      if (root) return [root]
    }
    return resolvedScopeIds
      .map((id) => workspaces.find((w) => w.id === id)?.contentRoot)
      .filter((p): p is string => p != null)
  }, [resolvedScopeIds, workspaces, viewContext])

  /** Extra content roots implied by open doc, tagged refs, and Product Board (widens search without manual workspace toggles). */
  const referenceScopeRoots = useMemo(() => {
    const roots = new Set<string>()
    const openDocRoot = chatScopeRootFromDocumentPath(activeDocumentPath, workspaces)
    if (openDocRoot) roots.add(openDocRoot)
    for (const r of references) {
      const raw =
        r.type === 'folder' || r.kind === 'folder'
          ? (r.path || r.id || '')
          : (r.path || '')
      const root = chatScopeRootFromDocumentPath(raw, workspaces) ?? chatContentRootFromRefPath(raw)
      if (root) roots.add(root)
    }
    if (viewContext?.type === 'product_board' && viewContext.openBetPath) {
      const root =
        chatScopeRootFromDocumentPath(viewContext.openBetPath, workspaces) ??
        chatContentRootFromRefPath(viewContext.openBetPath)
      if (root) roots.add(root)
    }
    return Array.from(roots)
  }, [activeDocumentPath, references, viewContext, workspaces])

  const mentionOpenTabIds = useMemo(
    () => filterOpenTabIdsForSearch(openTabIds) ?? [],
    [openTabIds],
  )

  const mentionRecentlyViewedIds = useMemo(
    () => filterOpenTabIdsForSearch(recentlyViewedIds) ?? [],
    [recentlyViewedIds],
  )

  useEffect(() => {
    void prefetchDocumentSearchContext(mentionOpenTabIds)
  }, [mentionOpenTabIds])

  const workspaceScopePaths = useMemo(
    () => [...new Set([...workspaceScopePathsBase, ...referenceScopeRoots])],
    [workspaceScopePathsBase, referenceScopeRoots]
  )

  const referenceScopeRootsKey = useMemo(
    () => [...referenceScopeRoots].sort().join('|'),
    [referenceScopeRoots]
  )
  const workspaceScopePathsBaseKey = useMemo(() => workspaceScopePathsBase.join('|'), [workspaceScopePathsBase])

  const referenceScopeRootsRef = useRef(referenceScopeRoots)
  referenceScopeRootsRef.current = referenceScopeRoots
  const workspaceScopePathsBaseRef = useRef(workspaceScopePathsBase)
  workspaceScopePathsBaseRef.current = workspaceScopePathsBase

  /** Dedupe scope-widen toasts: at most once per root per conversation (effect may re-run on parent re-render). */
  const scopeWidenToastSeenRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    scopeWidenToastSeenRef.current.clear()
  }, [currentConversationId, streamingConversationId])

  useEffect(() => {
    // Silent on empty / new chats: open-doc and chip tags widening search to the
    // active workspace is expected. Only nudge when a thread already has turns
    // and scope grows beyond the sidebar baseline mid-conversation.
    const msgs = currentConversationId ? _legacyMessages : streamingMessages
    const midChat =
      isStreaming ||
      msgs.some((m) => m.role === 'user' || m.role === 'assistant')
    if (!midChat) return

    const roots = referenceScopeRootsRef.current
    const base = workspaceScopePathsBaseRef.current
    const norm = (r: string) => r.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')
    const baseNorm = new Set(base.map(norm))
    // Collapse slug / contentRoot aliases to one label key before toasting.
    const added = roots.filter((r) => !baseNorm.has(norm(r)) && !base.includes(r))
    if (added.length === 0) return
    const labelKey = formatChatScopeWidenLabels(added, workspaces)
    if (!labelKey || scopeWidenToastSeenRef.current.has(labelKey)) return
    for (const r of added) {
      scopeWidenToastSeenRef.current.add(norm(r))
    }
    scopeWidenToastSeenRef.current.add(labelKey)
    toast.message('Chat search scope widened', {
      description: `Including open document or tagged paths under: ${labelKey}`,
    })
  }, [
    referenceScopeRootsKey,
    workspaceScopePathsBaseKey,
    currentConversationId,
    streamingConversationId,
    workspaces,
    isStreaming,
    streamingMessages,
    _legacyMessages,
  ])

  /** Workspace slugs for current chat scope picker (same org ids as `workspaceScopePaths`). Used to filter `/` skills with `requiredWorkspaces`. */
  const chatScopeWorkspaceSlugs = useMemo(() => {
    if (viewContext?.type === 'basecamp-project' && viewContext.organizationId) {
      const slug = workspaces.find((w) => w.id === viewContext.organizationId)?.slug
      return slug ? [slug] : []
    }
    const baseSlugs = resolvedScopeIds
      .map((id) => workspaces.find((w) => w.id === id)?.slug)
      .filter((s): s is string => Boolean(s))
    const refSlugs = referenceScopeRoots
      .map((root) => workspaces.find((w) => w.contentRoot === root)?.slug)
      .filter((s): s is string => Boolean(s))
    return [...new Set([...baseSlugs, ...refSlugs])]
  }, [resolvedScopeIds, workspaces, viewContext, referenceScopeRoots])

  /** For `/` skill menu: org display name + sidebar color keyed by slug */
  const slashWorkspaceMetaBySlug = useMemo(() => {
    const m: Record<string, { name: string; color: string | null }> = {}
    for (const w of workspaces) {
      if (w.slug) m[w.slug] = { name: w.name, color: w.color }
    }
    return m
  }, [workspaces])

  /** Human-readable active chat scope for skill menu header */
  const slashChatScopeLabel = useMemo(() => {
    if (viewContext?.type === 'basecamp-project' && viewContext.organizationId) {
      return workspaces.find((w) => w.id === viewContext.organizationId)?.name?.trim() ?? ''
    }
    const names = resolvedScopeIds
      .map((id) => workspaces.find((w) => w.id === id)?.name)
      .map((n) => n?.trim())
      .filter((n): n is string => Boolean(n))
    return names.join(', ')
  }, [resolvedScopeIds, workspaces, viewContext])

  /** Org ids for workspace-skill listing. Chat scope only, not every org the user belongs to. */
  const skillScopeOrganizationIds = useMemo(() => {
    if (viewContext?.type === 'basecamp-project' && viewContext.organizationId) {
      return [viewContext.organizationId]
    }
    return resolvedScopeIds.filter(Boolean)
  }, [viewContext?.type, viewContext?.organizationId, resolvedScopeIds])

  const [availableSkills, setAvailableSkills] = useState<SlashSkillRow[]>([])
  const skillScopeOrganizationIdsKey = skillScopeOrganizationIds.join('|')
  useEffect(() => {
    const qs = `?organizationIds=${encodeURIComponent(skillScopeOrganizationIds.join(','))}`
    fetchWithAuth(`/api/skills${qs}`).then(r => r.json()).then(data => {
      if (data.success && Array.isArray(data.skills)) {
        setAvailableSkills(
          data.skills.map((s: SlashSkillRow & { requiredWorkspaces?: string[] }) => ({
            ...s,
            requiredWorkspaces: Array.isArray(s.requiredWorkspaces) ? s.requiredWorkspaces : [],
          }))
        )
      }
    }).catch(() => {})
  }, [skillScopeOrganizationIdsKey])

  const scopedAvailableSkills = useMemo(() => {
    if (!chatScopeWorkspaceSlugs.length) return availableSkills
    const allowed = new Set(chatScopeWorkspaceSlugs.map((s) => s.toLowerCase()))
    return availableSkills.filter((s) => {
      const req = s.requiredWorkspaces
      if (!req?.length) return true
      return req.some((slug) => allowed.has(String(slug).toLowerCase()))
    })
  }, [availableSkills, chatScopeWorkspaceSlugs])

  const skillOffer = useMemo(() => {
    if (activeChatSkill || !input.trim()) return null
    for (const skill of scopedAvailableSkills) {
      const span = findLiteralSkillKeywordSpan(input, skill.triggerKeywords)
      if (!span) continue
      return { id: skill.id, name: skill.name, keyword: span.keyword }
    }
    return null
  }, [input, activeChatSkill, scopedAvailableSkills])

  const hasChatMessages = useMemo(() => {
    const msgs = currentConversationId ? _legacyMessages : streamingMessages
    return msgs.some((m) => m.role === 'user' || m.role === 'assistant')
  }, [currentConversationId, _legacyMessages, streamingMessages])

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [feedbackModalDetail, setFeedbackModalDetail] = useState<ChatFeedbackOpenDetail>({})

  const feedbackMessageSnapshot = useMemo(() => {
    const msgs = currentConversationId ? _legacyMessages : streamingMessages
    return msgs
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }))
  }, [currentConversationId, _legacyMessages, streamingMessages])

  const sessionScratchConversationId =
    hostedLinkedConversationId ?? streamingConversationId ?? currentConversationId ?? null
  const flowAgentOrigin = useFlowAgentOrigin(sessionScratchConversationId)
  const [sessionFilesOpen, setSessionFilesOpen] = useState(false)
  const [sessionFileCount, setSessionFileCount] = useState(0)

  useEffect(() => {
    if (!sessionScratchConversationId) {
      setSessionFilesOpen(false)
      setSessionFileCount(0)
      return
    }
    try {
      setSessionFilesOpen(
        sessionStorage.getItem(`maater-session-files-open:${sessionScratchConversationId}`) === '1',
      )
    } catch {
      setSessionFilesOpen(false)
    }
  }, [sessionScratchConversationId])

  const handleSessionFilesOpenChange = useCallback(
    (open: boolean) => {
      setSessionFilesOpen(open)
      if (sessionScratchConversationId) {
        try {
          sessionStorage.setItem(
            `maater-session-files-open:${sessionScratchConversationId}`,
            open ? '1' : '0',
          )
        } catch {
          /* ignore quota / private mode */
        }
      }
    },
    [sessionScratchConversationId],
  )

  const feedbackConversationTitle = useMemo(() => {
    const convId =
      hostedLinkedConversationId ?? streamingConversationId ?? currentConversationId ?? null
    if (!convId) return _hostedThreadTitle ?? null
    return (
      activeConversations.find((c) => c.id === convId)?.title ??
      _hostedThreadTitle ??
      null
    )
  }, [
    activeConversations,
    hostedLinkedConversationId,
    currentConversationId,
    streamingConversationId,
    _hostedThreadTitle,
  ])

  const pendingProposedEdits = useMemo(() => {
    const msgs = currentConversationId ? _legacyMessages : streamingMessages
    const byDoc = new Map<string, ProposedEditData>()
    const order: string[] = []
    const anon: ProposedEditData[] = []
    for (let i = 0; i < msgs.length; i += 1) {
      const pe = msgs[i]?.proposedEdit
      if (!isPendingProposedEdit(pe)) continue
      const id = pe!.documentId ? String(pe!.documentId) : ''
      if (!id) {
        anon.push(pe!)
        continue
      }
      if (!byDoc.has(id)) order.push(id)
      const prev = byDoc.get(id)
      byDoc.set(id, {
        ...pe!,
        // Keep earliest original so multi-patch review stays one cumulative diff.
        original: prev?.original ?? pe!.original,
      })
    }
    return [...order.map((id) => byDoc.get(id)!), ...anon]
  }, [currentConversationId, _legacyMessages, streamingMessages])

  /** Keep dismissals for Files bar - history merge must honor these or Keep is a no-op. */
  const [dismissedModifiedFileKeys, setDismissedModifiedFileKeys] = useState<Set<string>>(
    () => new Set(),
  )

  const modifiedFilesForPanel = useMemo(() => {
    const historyFiles = completedFilesFromMessages(
      streamingMessages.length > 0 ? streamingMessages : _legacyMessages,
    ).map((f) => ({
      filePath: f.filePath,
      displayPath: f.displayPath,
      url: f.url,
      documentId: f.documentId,
      title: f.title,
      changeStatus: f.changeStatus,
      status: f.status ?? ('modified' as const),
      linesAdded: f.linesAdded ?? 0,
      linesRemoved: f.linesRemoved ?? 0,
    }))
    const merged = coalesceCompletedFiles([...historyFiles, ...completedFiles])
    const dismissed = dismissedModifiedFileKeys
    const visible =
      dismissed.size === 0
        ? merged
        : merged.filter((f) => {
            const key = f.documentId || f.filePath
            return !dismissed.has(key)
          })
    return buildChatModifiedFilesPanelRows(pendingProposedEdits, visible)
  }, [
    pendingProposedEdits,
    completedFiles,
    streamingMessages,
    _legacyMessages,
    dismissedModifiedFileKeys,
  ])

  const [batchEditReview, setBatchEditReview] = useState<{
    edits: ProposedEditData[]
    index: number
  } | null>(null)
  const [documentReview, setDocumentReview] = useState<ProposedEditData | null>(null)
  const [documentReviewHost, setDocumentReviewHost] = useState<HTMLElement | null>(null)
  const [documentReviewScroll, setDocumentReviewScroll] = useState(0)
  const pendingReviewReadyFor = useRef<string | null>(null)

  const openBatchEditReview = useCallback(
    (edits: ProposedEditData[], startIndex = 0) => {
      const pending = edits.filter(isPendingProposedEdit)
      if (pending.length === 0) return
      const safeIndex = Math.min(Math.max(startIndex, 0), pending.length - 1)
      setBatchEditReview({ edits: pending, index: safeIndex })
      setProposedEdit(pending[safeIndex])
      setDiffPreviewOpen(true)
    },
    [setProposedEdit, setDiffPreviewOpen],
  )

  const closeBatchEditReview = useCallback(() => {
    setBatchEditReview(null)
    setDiffPreviewOpen(false)
    setProposedEdit(null)
  }, [setDiffPreviewOpen, setProposedEdit])

  const advanceBatchEditReview = useCallback(
    (fromIndex: number, direction: 1 | -1) => {
      if (!batchEditReview) return
      const nextIndex = fromIndex + direction
      if (nextIndex < 0 || nextIndex >= batchEditReview.edits.length) {
        closeBatchEditReview()
        return
      }
      setBatchEditReview({ ...batchEditReview, index: nextIndex })
      setProposedEdit(batchEditReview.edits[nextIndex])
    },
    [batchEditReview, closeBatchEditReview, setProposedEdit],
  )

  const handleStreamProposedEditsComplete = useCallback(
    (edits: ProposedEditData[]) => {
      const pending = edits.filter(isPendingProposedEdit)
      if (pending.length >= 1) {
        setModifiedFilesPanelExpanded(true)
      }
      if (pending.length >= 2) {
        openBatchEditReview(pending, 0)
      }
    },
    [openBatchEditReview, setModifiedFilesPanelExpanded],
  )

  const openPendingEditDiff = useCallback(
    (edit?: ProposedEditData): boolean => {
      const viewable = pendingProposedEdits.filter((e) => {
        if (!isPendingProposedEdit(e)) return false
        const original = e.original ?? ''
        const proposed = e.proposed ?? ''
        return Boolean(original || proposed) && original !== proposed
      })
      if (viewable.length === 0) return false
      if (viewable.length > 1) {
        const idx = edit
          ? viewable.findIndex((e) => proposedEditMatches(e, edit))
          : 0
        openBatchEditReview(viewable, idx >= 0 ? idx : 0)
        return true
      }
      const target =
        (edit && viewable.find((e) => proposedEditMatches(e, edit))) || viewable[0]
      setBatchEditReview(null)
      setProposedEdit(target)
      setDiffPreviewOpen(false)
      setDocumentReview(target)
      setDocumentReviewScroll((n) => n + 1)
      if (target.documentId) {
        window.dispatchEvent(
          new CustomEvent('open-document', { detail: { documentId: target.documentId } }),
        )
      }
      return true
    },
    [pendingProposedEdits, openBatchEditReview, setProposedEdit, setDiffPreviewOpen],
  )

  useEffect(() => {
    if (!documentReview) {
      setDocumentReviewHost(null)
      return
    }
    const readingId = testIds.documentViewer.readingPane
    const find = () =>
      document.querySelector(`[data-testid="${readingId}"]`) ??
      document.querySelector(`[data-testid="${testIds.resizableLayout.mainArea}"]`)
    const apply = () => {
      const host = find()
      const resolved = host instanceof HTMLElement ? host : document.body
      setDocumentReviewHost(resolved)
      return host instanceof HTMLElement && host.getAttribute('data-testid') === readingId
    }
    if (apply()) return
    const timer = window.setInterval(() => {
      if (apply()) window.clearInterval(timer)
    }, 120)
    const stop = window.setTimeout(() => window.clearInterval(timer), 2500)
    return () => {
      window.clearInterval(timer)
      window.clearTimeout(stop)
    }
  }, [documentReview])

  useEffect(() => {
    const conversationId = streamingConversationId
    if (!conversationId) return
    if (streamingMessages.length === 0) return

    const openRestored = (edits: ProposedEditData[]) => {
      const activeId = activeDocumentIdRef.current
      const target =
        edits.find((edit) => edit.documentId === activeId) ||
        (edits.length === 1 ? edits[0] : null)
      if (!target) return
      setBatchEditReview(null)
      setProposedEdit(target)
      setDiffPreviewOpen(false)
      setDocumentReview(target)
      setDocumentReviewScroll((n) => n + 1)
      window.dispatchEvent(
        new CustomEvent('open-document', { detail: { documentId: target.documentId } }),
      )
    }

    if (pendingReviewReadyFor.current !== conversationId) {
      pendingReviewReadyFor.current = conversationId
      const stored = readPendingProposedEdits(conversationId)
      if (stored) {
        const next = replacePendingProposedEditCards(streamingMessages, stored)
        if (next !== streamingMessages) setStreamingMessages(next)
        if (stored.length > 0) openRestored(stored)
        return
      }
      const fromMessages = pendingProposedEditsInMessages(streamingMessages)
      if (fromMessages.length > 0) openRestored(fromMessages)
    }

    const live = pendingProposedEditsInMessages(streamingMessages)
    const signature = live
      .map((edit) => `${edit.sessionEditId || edit.documentId}\0${edit.original}\0${edit.proposed}`)
      .join('\n')
    if (pendingReviewSignature.get(conversationId) === signature) return
    pendingReviewSignature.set(conversationId, signature)
    writePendingProposedEdits(conversationId, live)
    for (const message of streamingMessages) {
      if (!message.messageId || !Array.isArray(message.metadata?.pendingProposedEdits)) continue
      void patchChatMessageMetadata(message.messageId, { pendingProposedEdits: live })
    }
  }, [
    streamingConversationId,
    streamingMessages,
    activeDocumentIdRef,
    setProposedEdit,
    setDiffPreviewOpen,
    setStreamingMessages,
  ])

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ documentId?: string }>).detail ?? {}
      const match = detail.documentId
        ? pendingProposedEdits.find((e) => e.documentId === detail.documentId)
        : undefined
      openPendingEditDiff(match ?? pendingProposedEdits[0])
    }
    window.addEventListener('flownotes:review-proposed-edit', handler)
    return () => window.removeEventListener('flownotes:review-proposed-edit', handler)
  }, [pendingProposedEdits, openPendingEditDiff])

  const handleOpenChatFeedback = useCallback(
    (category?: ChatFeedbackCategory, extra?: Partial<ChatFeedbackOpenDetail>) => {
      if (collapsed) onToggleCollapse?.()

      const resolvedCategory =
        category ?? (hasChatMessages ? 'agent-issue' : 'feedback')
      const detail: ChatFeedbackOpenDetail = {
        category: resolvedCategory,
        conversationId:
          extra?.conversationId ??
          hostedLinkedConversationId ??
          streamingConversationId ??
          currentConversationId ??
          null,
        threadId: extra?.threadId ?? hostedThreadId ?? null,
        model: extra?.model ?? selectedModel ?? null,
        provider: extra?.provider ?? selectedProvider ?? null,
        note: extra?.note,
      }

      setFeedbackModalDetail(detail)
      setFeedbackModalOpen(true)
    },
    [
      collapsed,
      onToggleCollapse,
      hasChatMessages,
      hostedLinkedConversationId,
      streamingConversationId,
      currentConversationId,
      hostedThreadId,
      selectedModel,
      selectedProvider,
    ],
  )

  const handleFeedbackFiled = useCallback(
    (_result: { documentId: string; filePath: string }) => {
      // Modal shows its own success screen - no toast needed
    },
    [],
  )

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ChatFeedbackOpenDetail>).detail ?? {}
      // Route events to the right pane instance:
      // - Events with threadId come from editor-tab toolbars → only the matching editor pane handles it
      // - Events without threadId come from the sidebar menu → only the sidebar pane (no hostedThreadId) handles it
      if (detail.threadId) {
        if (detail.threadId !== hostedThreadId) return
      } else {
        if (hostedThreadId) return
      }
      handleOpenChatFeedback(detail.category, detail)
    }
    window.addEventListener(chatFeedbackOpenEventName(), handler)
    return () => window.removeEventListener(chatFeedbackOpenEventName(), handler)
  }, [handleOpenChatFeedback, hostedThreadId])

  const userExplicitlySetScope = useRef(false)
  /** Content roots from last loaded conversation; applied to draft scope when workspaces are ready. */
  const lastLoadedScopePathsRef = useRef<string[] | null>(null)
  const workspacesRef = useRef(workspaces)
  workspacesRef.current = workspaces

  const handleScopeChange = useCallback((scope: string[] | null) => {
    userExplicitlySetScope.current = true
    setConversationScope(scope)
  }, [])

  // Dismissed active-document IDs for AI Context bar, persisted per conversation in sessionStorage.
  const [dismissedDocIds, setDismissedDocIds] = useState<Set<string>>(() =>
    loadDismissedDocIds(streamingConversationId)
  )
  const prevStreamingConvForDismissalsRef = useRef<string | null>(streamingConversationId)
  useEffect(() => {
    const prev = prevStreamingConvForDismissalsRef.current
    const curr = streamingConversationId
    prevStreamingConvForDismissalsRef.current = curr

    if (curr && prev === null) {
      const homeIds = loadDismissedDocIds(null)
      const existing = loadDismissedDocIds(curr)
      if (homeIds.size > 0) {
        const merged = new Set([...existing, ...homeIds])
        persistDismissedDocIds(curr, merged)
        clearDismissedDocIdsForConversation(null)
        setDismissedDocIds(merged)
        return
      }
    }

    setDismissedDocIds(loadDismissedDocIds(curr))
  }, [streamingConversationId])

  const dismissedDocIdsRef = useRef<Set<string>>(dismissedDocIds)
  useEffect(() => {
    dismissedDocIdsRef.current = dismissedDocIds
  }, [dismissedDocIds])

  // Native HTML5 drag tracking for drop zone overlay.
  // Do not use an enter/leave counter - Chrome often fires extra enters with
  // relatedTarget=null, so the count never returns to 0 and the overlay sticks.
  const lastNativeDragOverAtRef = useRef(0)
  const [isNativeDragOver, setIsNativeDragOver] = useState(false)

  const resetNativeDragOverlay = useCallback(() => {
    lastNativeDragOverAtRef.current = 0
    setIsNativeDragOver(false)
  }, [])

  const markNativeDragOver = useCallback(() => {
    lastNativeDragOverAtRef.current = Date.now()
    setIsNativeDragOver(true)
  }, [])

  // OS file drags often never fire dragend; capture drop/dragend + a dragover watchdog.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetNativeDragOverlay()
    }
    window.addEventListener('dragend', resetNativeDragOverlay, true)
    window.addEventListener('drop', resetNativeDragOverlay, true)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('dragend', resetNativeDragOverlay, true)
      window.removeEventListener('drop', resetNativeDragOverlay, true)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [resetNativeDragOverlay])

  useEffect(() => {
    if (!isNativeDragOver) return
    const id = window.setInterval(() => {
      if (Date.now() - lastNativeDragOverAtRef.current > 300) {
        resetNativeDragOverlay()
      }
    }, 150)
    return () => window.clearInterval(id)
  }, [isNativeDragOver, resetNativeDragOverlay])

  const handleNativeDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    const types = Array.from(e.dataTransfer?.types ?? [])
    const looksLikeReferenceDrag =
      types.includes('application/json') ||
      types.includes('Files') ||
      types.includes('text/uri-list') ||
      types.includes('text/plain')
    if (!looksLikeReferenceDrag) return
    markNativeDragOver()
  }
  const handleNativeDragLeave = (e: React.DragEvent) => {
    const cur = e.currentTarget as HTMLElement
    const related = e.relatedTarget as Node | null
    if (related && cur.contains(related)) return
    const r = cur.getBoundingClientRect()
    const { clientX: x, clientY: y } = e
    const stillInside = x > r.left && x < r.right && y > r.top && y < r.bottom
    if (!related && stillInside) return
    resetNativeDragOverlay()
  }
  const handleNativeDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    markNativeDragOver()
  }
  const parseDragJsonToReference = useCallback((raw: string): DocumentReference | null => {
    if (!raw?.trim()) return null
    try {
      const data = JSON.parse(raw) as Record<string, unknown>
      const id = typeof data.id === 'string' ? data.id : null
      if (!id) return null
      const path = typeof data.path === 'string' ? data.path : undefined
      const title = (typeof data.title === 'string' ? data.title : typeof data.name === 'string' ? data.name : null) || 'Document'
      const nodeType = data.nodeType === 'folder' ? 'folder' : 'file'
      const docTypeRaw = typeof data.documentType === 'string' ? data.documentType : 'note'
      const type = (
        ['work_spec', 'note', 'voice_memo', 'meeting', 'idea', 'research', 'todo', 'folder'].includes(docTypeRaw)
          ? docTypeRaw
          : 'note'
      ) as DocumentReference['type']
      return {
        id,
        title,
        type: nodeType === 'folder' ? 'folder' : type,
        path,
        kind: nodeType === 'folder' ? 'folder' : 'document',
      }
    } catch {
      return null
    }
  }, [])

  /** Capture-phase drop: whole chat pane accepts tab/sidebar JSON drops; input keeps its own drop for cursor placement */
  const handleChatDropCapture = useCallback(
    (e: React.DragEvent) => {
      // Always clear sticky overlay first - early returns used to leave it stuck on prod.
      resetNativeDragOverlay()

      const target = e.target as Node
      const editorEl = contentEditableInputRef.current?.editorRef?.current ?? null
      if (editorEl && (target === editorEl || editorEl.contains(target))) {
        // Drop is handled by the editor.
        return
      }

      const dtFiles = e.dataTransfer.files
      if (dtFiles && dtFiles.length > 0) {
        e.preventDefault()
        e.stopPropagation()
        if (collapsed) {
          onToggleCollapse?.()
        }
        const applied = contentEditableInputRef.current?.applyNativeFileDrop(dtFiles) ?? false
        if (!applied) {
          toast.info('That file type cannot be dropped into chat', {
            description: 'Drop images, audio (.m4a, .mp3, .wav), or .md / .txt files, or drag documents from the sidebar.',
          })
        }
        return
      }

      const raw = e.dataTransfer.getData('application/json')
      const ref = parseDragJsonToReference(raw)
      if (!ref) return
      e.preventDefault()
      e.stopPropagation()
      if (collapsed) {
        onToggleCollapse?.()
      }
      onAddReference?.(ref)
      const insert = () => contentEditableInputRef.current?.insertReference(ref)
      if (collapsed) {
        requestAnimationFrame(() => requestAnimationFrame(insert))
      } else {
        insert()
      }
    },
    [collapsed, onAddReference, onToggleCollapse, parseDragJsonToReference, resetNativeDragOverlay]
  )

  const handleChatDragOverCapture = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    lastNativeDragOverAtRef.current = Date.now()
  }, [])

  // Drag & drop
  const { setNodeRef, isOver } = useDroppable({
    id: "chat-input",
  })

  // Use hooks for drag & drop and reference sync
  useDragAndDrop(contentEditableInputRef, isOver)
  useReferenceSync(references, contentEditableInputRef, prevReferencesRef)

  const openReferenceFromChat = useCallback(
    (ref: DocumentReference, event: unknown) => {
      if (ref.kind === 'folder' || ref.type === 'folder') {
        const p = ref.path ?? ref.id
        if (p) onFolderClick?.(p)
        return
      }
      const idOrPath = ref.path ?? ref.id
      onDocumentClick?.(idOrPath, event as ReactMouseEvent)
    },
    [onDocumentClick, onFolderClick]
  )

  const handleReferencesChange = useCallback(
    (nextRefs: DocumentReference[]) => {
      for (const ref of nextRefs) {
        if (!referenceListedWithSameKey(references, ref)) {
          onAddReference?.(ref)
        }
      }
      for (const ref of references) {
        if (!referenceListedWithSameKey(nextRefs, ref)) {
          onRemoveReference?.(ref.id, ref)
        }
      }
    },
    [references, onAddReference, onRemoveReference],
  )

  const handleLocalTextFilesDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return false
      try {
        const { importLocalTextFiles, isLocalTextImportFile, resolveChatImportLandingFolder } =
          await import('@/lib/local-text-import')
        const { createDocument } = await import('@/lib/api')
        const valid: File[] = []
        const rejected: string[] = []
        for (const f of files) {
          const check = isLocalTextImportFile(f)
          if (check.ok) valid.push(f)
          else rejected.push(check.reason)
        }
        if (rejected.length > 0) {
          toast.error('Some files were skipped', {
            description: rejected.slice(0, 3).join('; '),
          })
        }
        if (valid.length === 0) return false

        const folderPath = resolveChatImportLandingFolder(workspaceScopePaths[0] ?? '')
        const workspaceId = resolvedScopeIds[0]
        const { imported, errors } = await importLocalTextFiles({
          files: valid,
          folderPath,
          workspaceId,
          createDocument,
        })

        for (const doc of imported) {
          const ref: DocumentReference = {
            id: doc.id,
            title: doc.title,
            type: 'note',
            path: doc.filePath,
            kind: 'document',
          }
          onAddReference?.(ref)
          contentEditableInputRef.current?.insertReference(ref)
          window.dispatchEvent(
            new CustomEvent('document-created', {
              detail: { path: doc.filePath || doc.id, folderPath },
            }),
          )
        }

        if (imported.length > 0) {
          toast.success(
            imported.length === 1 ? 'Imported and attached' : `Imported ${imported.length} files`,
            {
              description: 'Document chips added to chat. Send a message to discuss or organize.',
            },
          )
        }
        if (errors.length > 0) {
          toast.error('Import failed', {
            description: errors.map((e) => `${e.name}: ${e.reason}`).slice(0, 3).join('; '),
          })
        }
        return imported.length > 0
      } catch (err) {
        toast.error('Failed to import file', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
        return false
      }
    },
    [workspaceScopePaths, resolvedScopeIds, onAddReference],
  )

  const handleLocalAudioFilesDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return false
      const toastId = 'chat-audio-drop'
      try {
        const {
          ingestDroppedAudioFiles,
          formatVoiceMemoComposerInsert,
          workspaceVoiceMemoFolder,
        } = await import('@/lib/chat-audio-import')
        const { uploadVoiceMemos, processVoiceMemo } = await import('@/lib/api/voice-memos')
        const folderPath = workspaceVoiceMemoFolder(workspaceScopePaths[0] ?? '')
        toast.loading(
          files.length === 1 ? `Transcribing ${files[0].name}...` : `Transcribing ${files.length} audio files...`,
          { id: toastId },
        )
        const { ingested, rejected, errors } = await ingestDroppedAudioFiles({
          files,
          folderPath,
          upload: uploadVoiceMemos,
          process: processVoiceMemo,
        })
        if (rejected.length > 0) {
          toast.error('Some audio files were skipped', {
            description: rejected.slice(0, 3).join('; '),
          })
        }
        for (const item of ingested) {
          const doc = item.document
          if (doc?.id) {
            const ref: DocumentReference = {
              id: doc.id,
              title: doc.title || item.originalName,
              type: 'voice_memo',
              path: doc.filePath,
              kind: 'document',
            }
            onAddReference?.(ref)
            contentEditableInputRef.current?.insertReference(ref)
            window.dispatchEvent(
              new CustomEvent('document-created', {
                detail: { path: doc.filePath || doc.id, folderPath },
              }),
            )
          }
          contentEditableInputRef.current?.appendComposerText(formatVoiceMemoComposerInsert(item))
        }
        if (ingested.length > 0) {
          toast.success(
            ingested.length === 1 ? 'Transcript added to chat' : `Added ${ingested.length} transcripts`,
            {
              id: toastId,
              description: ingested.some((i) => i.document?.id)
                ? 'Filed as a voice memo and attached. Send to discuss.'
                : 'Transcript is in the composer. Send to discuss.',
            },
          )
        } else {
          toast.dismiss(toastId)
        }
        if (errors.length > 0) {
          toast.error('Audio import failed', {
            description: errors.map((e) => `${e.name}: ${e.reason}`).slice(0, 3).join('; '),
          })
        }
        return ingested.length > 0
      } catch (err) {
        toast.error('Failed to transcribe audio', {
          id: toastId,
          description: err instanceof Error ? err.message : 'Unknown error',
        })
        return false
      }
    },
    [workspaceScopePaths, onAddReference],
  )

  const handleLongTextPaste = useCallback(
    async (text: string) => {
      try {
        const { importPastedTextAsDocument, resolveChatImportLandingFolder } = await import(
          '@/lib/local-text-import'
        )
        const { createDocument } = await import('@/lib/api')
        const folderPath = resolveChatImportLandingFolder(workspaceScopePaths[0] ?? '')
        const workspaceId = resolvedScopeIds[0]
        const doc = await importPastedTextAsDocument({
          text,
          folderPath,
          workspaceId,
          createDocument,
        })
        const ref: DocumentReference = {
          id: doc.id,
          title: doc.title,
          type: 'note',
          path: doc.filePath,
          kind: 'document',
        }
        onAddReference?.(ref)
        contentEditableInputRef.current?.insertReference(ref)
        window.dispatchEvent(
          new CustomEvent('document-created', {
            detail: { path: doc.filePath || doc.id, folderPath },
          }),
        )
        toast.success('Pasted text saved as document', {
          description: 'Chip added to chat. Send a message to discuss.',
        })
        return true
      } catch (err) {
        toast.error('Could not save pasted text', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
        return false
      }
    },
    [workspaceScopePaths, resolvedScopeIds, onAddReference],
  )

  // LLM provider hook
  const { handleProviderChange: _handleProviderChange, handleModelChange } = useLLMProvider(
    selectedProvider,
    setSelectedProvider,
    selectedModel,
    setSelectedModel,
    setAvailableModels,
    setProvidersAvailable,
    setAllModelsByProvider,
    allModelsByProvider,
    availableModels
  )

  /** Model selector + optional provider badges: lives in composer toolbar (narrow-safe flex). */
  const composerModelCluster = useMemo(() => {
    const resolvedModelId = resolveChatModelId(
      selectedModel,
      availableModels,
      allModelsByProvider,
    )
    const fullName = fullChatModelDisplayName({
      selectedModel: resolvedModelId,
      availableModels,
      allModelsByProvider,
    })
    const shortTriggerLabel = shortChatModelDisplayName(fullName)
    return (
      <>
        <ChatContextMeter usage={contextUsage} />
        <Select value={resolvedModelId} onValueChange={handleModelChange}>
          <SelectTrigger
            data-testid={testIds.chatPane.modelSelector}
            className={cn(
              "h-8 w-auto min-w-0 shrink gap-1 border-border bg-background px-1.5 text-xs shadow-xs",
              "@[300px]/compose:max-w-[min(100%,11rem)] @[300px]/compose:gap-1.5 @[300px]/compose:px-2",
              "justify-start hover:bg-muted",
              "[&>svg:last-child]:hidden @[300px]/compose:[&>svg:last-child]:block",
            )}
            title={fullName}
            aria-label={`Model: ${shortTriggerLabel}`}
          >
            <Brain className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            <span className="!hidden min-w-0 truncate text-left text-xs @[300px]/compose:!inline">
              {shortTriggerLabel || 'Select model'}
            </span>
          </SelectTrigger>
          <SelectContent>
            {Object.entries(allModelsByProvider).map(([providerId, models]) => {
              const provider = providersAvailable.find((p: { id: string }) => p.id === providerId)
              return (
                <SelectGroup key={providerId}>
                  <SelectLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {provider?.name || providerId}
                  </SelectLabel>
                  {(models as { id: string; name: string }[]).map((model) => (
                    <SelectItem key={model.id} value={model.id} className="whitespace-nowrap pl-6">
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )
            })}
          </SelectContent>
        </Select>
        {selectedProvider === "ollama" && (() => {
          const currentModel = availableModels.find((m: { id: string }) => m.id === resolvedModelId)
          const hasTools = currentModel?.supportsTools
          return (
            <Badge
              variant="outline"
              className={cn(
                'hidden @[360px]/compose:inline-flex',
                hasTools
                  ? "bg-blue-500/10 text-blue-500 border-blue-500/20 text-[10px]"
                  : "bg-green-500/10 text-green-500 border-green-500/20 text-[10px]"
              )}
            >
              <Zap className="h-3 w-3 mr-0.5 shrink-0" aria-hidden />
              {hasTools ? "Tools" : "Local"}
            </Badge>
          )
        })()}
        {selectedProvider === "localai" && (
          <Badge variant="outline" className="hidden @[360px]/compose:inline-flex bg-blue-500/10 text-blue-500 border-blue-500/20 text-[10px] shrink-0">
            <Zap className="h-3 w-3 mr-0.5 shrink-0" aria-hidden />
            Tools
          </Badge>
        )}
        {selectedProvider === "openai" && (
          <span className="text-[10px] text-muted-foreground shrink-0 hidden @[320px]/compose:inline">
            Cloud
          </span>
        )}
      </>
    )
  }, [
    selectedModel,
    handleModelChange,
    availableModels,
    allModelsByProvider,
    providersAvailable,
    selectedProvider,
    contextUsage,
  ])

  const handleDismissActiveDocument = useCallback((documentId: string) => {
    setDismissedDocIds((prev) => {
      const next = new Set(prev).add(documentId)
      persistDismissedDocIds(streamingConversationId, next)
      return next
    })
  }, [streamingConversationId])

  // Conversation polling hook
  const { startPolling, stopPolling } = useConversationPolling(
    setIsPolling,
    pollingIntervalRef,
    messagesContainerRef,
    setLegacyMessages,
    isNearBottom,
    userHasScrolled,
    setThinkingModeActive,
    setIsTyping,
    setHistoryRefreshKey,
  )

  const activateIngestedDocument = useCallback(
    (documentId: string) => {
      onDocumentClick?.(documentId)
    },
    [onDocumentClick],
  )

  const ingestionLegacyConversationIdRef = useRef<string | null>(null)
  useEffect(() => {
    ingestionLegacyConversationIdRef.current = currentConversationId
  }, [currentConversationId])

  const stopPollingRef = useRef(stopPolling)
  useEffect(() => {
    stopPollingRef.current = stopPolling
  }, [stopPolling])

  useEffect(() => {
    const onIngestionComplete = async (ev: Event) => {
      const detail = (ev as CustomEvent<BasecampIngestionCompleteDetail>).detail ?? {}
      const cid = detail.conversationId
      if (!cid || cid !== ingestionLegacyConversationIdRef.current) return
      try {
        const result = await getConversation(cid, true)
        await flipIngestionConversationToStreaming({
          stopPolling: () => stopPollingRef.current(),
          setCurrentConversationId,
          setStreamingConversationId,
          setStreamingMessages,
          setLegacyMessages,
          setThinkingModeActive,
          conversationId: cid,
          conversation: result.conversation,
          filePathHint: detail.filePath,
          openIngestedDocument: false,
          onActivateIngestedDocument: activateIngestedDocument,
        })
      } catch (err) {
        console.error('[ChatPane] Failed to flip ingestion thread to streaming:', err)
      }
    }
    window.addEventListener(BASECAMP_INGESTION_COMPLETE, onIngestionComplete as EventListener)
    return () =>
      window.removeEventListener(BASECAMP_INGESTION_COMPLETE, onIngestionComplete as EventListener)
  }, [
    activateIngestedDocument,
    setCurrentConversationId,
    setStreamingConversationId,
    setStreamingMessages,
    setLegacyMessages,
    setThinkingModeActive,
  ])

  const isProgrammaticScrollRef = useRef(false)
  // Breakaway flag for stream follow - do NOT mirror React state each render.
  // Progressive reveal re-renders every ~14ms with stale userHasScrolled=false and would
  // clobber a wheel/touch breakaway before the state update commits.
  const userHasScrolledRef = useRef(false)

  // Use scroll detection hook
  useScrollDetection({
    messagesContainerRef,
    setIsNearBottom,
    setUserHasScrolled,
    userHasScrolledRef,
    isProgrammaticScrollRef,
    contentKey: `${streamingMessages.length}:${streamingMessages[streamingMessages.length - 1]?.content?.length ?? 0}`,
  })

  // Follow latest stream line (sticky prompt keeps the user question visible)
  const streamFollowKey = useMemo(() => {
    if (!isStreaming) return 'idle'
    const last = streamingMessages[streamingMessages.length - 1]
    const contentLen = last?.content?.length ?? 0
    return `${streamingMessages.length}:${last?.role ?? ''}:${contentLen}:${isTyping ? 1 : 0}`
  }, [isStreaming, streamingMessages, isTyping])

  useStreamFollowScroll({
    messagesContainerRef,
    isStreaming,
    userHasScrolled,
    userHasScrolledRef,
    followKey: streamFollowKey,
    isProgrammaticScrollRef,
    setUserHasScrolled,
    setIsNearBottom,
  })

  // Get handlers from hooks
  const chatHandlers = useChatHandlers(
    streamingMessages,
    setStreamingMessages,
    streamingInput,
    setStreamingInput,
    setIsStreaming,
    setIsTyping,
    abortControllerRef,
    streamingConversationId,
    setStreamingConversationId,
    references,
    activeDocumentIdRef,
    selectedProvider,
    selectedModel,
    dismissedDocIdsRef,
    activeConversations,
    setActiveConversations,
    setHistoryRefreshKey,
    messagesContainerRef,
    onClearReferences,
    contentEditableInputRef,
    activeSkillIdRef,
    activeSkillRef
  )

  const editHandlers = useEditHandlers(
    proposedEdit,
    setProposedEdit,
    setStreamingMessages,
    setIsApplyingEdit,
    setDiffPreviewOpen,
    streamingConversationId,
    activeDocumentIdRef,
    setCompletedFiles
  )

  const conversationHandlers = useConversationHandlers(
    setCurrentConversationId,
    setStreamingConversationId,
    setStreamingMessages,
    setLegacyMessages,
    setStreamingInput,
    setLegacyInput,
    setShowHistory,
    setActiveConversations,
    setHistoryRefreshKey,
    currentConversationId,
    pollingIntervalRef,
    setIsPolling,
    deletingConversationsRef,
    startPolling,
    stopPolling,
    setConversationScope,
    lastLoadedScopePathsRef,
    setThinkingModeActive,
    setCompletedFiles,
    streamingConversationId,
    hostedLinkedConversationId,
    abortControllerRef,
    setIsStreaming,
    setIsTyping,
    streamEpochRef,
    setPendingBasecampDraft,
    setPendingBasecampCreateDraft,
    streamInFlightRef,
    hostedThreadId,
    userExplicitlySetScope,
    workspacesRef,
    committedUserTurnRef,
  )

  // Destructure handlers for easier access
  const {
    handleStop: handleStopBase,
    handleSuggestedPrompt,
    handleCopyCode,
    saveMessageToHistory,
  } = chatHandlers

  /** Track if user explicitly stopped (vs natural end) - affects queue flush. */
  const userStoppedRef = useRef(false)
  /** Track Send now pending item - takes precedence over natural queue flush. */
  const sendNowPendingRef = useRef<QueuedChatMessage | null>(null)

  /** Stop only the current stream; keep the queue so handleSend's finally can flush the next item. */
  const handleStop = useCallback(() => {
    streamInFlightRef.current = false
    userStoppedRef.current = true
    handleStopBase()
  }, [handleStopBase, streamInFlightRef])

  const {
    handleAcceptEdit,
    handleRejectEdit,
    handleEditBeforeApply,
  } = editHandlers

  const handleDocumentReviewDecide = useCallback(
    async (decision: {
      kind: 'accepted' | 'rejected'
      saved: string
      proposed: string
      pending: number
    }) => {
      if (!documentReview) return false
      if (decision.kind === 'rejected' && decision.pending === 0) {
        const ok = await handleRejectEdit(documentReview)
        if (ok) setDocumentReview(null)
        return ok
      }
      if (decision.kind === 'rejected') {
        const next: ProposedEditData = {
          ...documentReview,
          original: decision.saved,
          proposed: decision.proposed,
          status: 'pending',
        }
        setDocumentReview(decision.pending > 0 ? next : null)
        setProposedEdit(next)
        setStreamingMessages((prev) =>
          prev.map((message) =>
            message.proposedEdit?.documentId === documentReview.documentId
              ? {
                  ...message,
                  proposedEdit: {
                    ...message.proposedEdit,
                    original: decision.saved,
                    proposed: decision.proposed,
                    status: 'pending',
                  },
                }
              : message,
          ),
        )
        return true
      }
      const stillPending = decision.pending > 0
      const ok = await handleAcceptEdit(documentReview, {
        newContent: decision.saved,
        keepPending: stillPending,
        nextOriginal: decision.saved,
        nextProposed: decision.proposed,
      })
      if (!ok) return false
      setDocumentReview(
        stillPending
          ? {
              ...documentReview,
              original: decision.saved,
              proposed: decision.proposed,
              status: 'pending',
            }
          : null,
      )
      return true
    },
    [documentReview, handleAcceptEdit, handleRejectEdit, setProposedEdit, setStreamingMessages],
  )

  const closeReviewForEdit = useCallback(
    (edit?: ProposedEditData) => {
      if (!edit || !documentReview) return
      if (!proposedEditMatches(documentReview, edit)) return
      setDocumentReview(null)
    },
    [documentReview],
  )

  const acceptFromChrome = useCallback(
    async (edit?: ProposedEditData, options?: EditHandlerOptions) => {
      const ok = await handleAcceptEdit(edit, options)
      if (ok) closeReviewForEdit(edit)
      return ok
    },
    [closeReviewForEdit, handleAcceptEdit],
  )

  const rejectFromChrome = useCallback(
    async (edit?: ProposedEditData, options?: EditHandlerOptions) => {
      const ok = await handleRejectEdit(edit, options)
      if (ok) closeReviewForEdit(edit)
      return ok
    },
    [closeReviewForEdit, handleRejectEdit],
  )

  const applyBatchEditDecision = useCallback(
    async (decision: 'accept' | 'reject', edit: ProposedEditData) => {
      const inBatch = batchEditReview != null && batchEditReview.edits.length > 1
      const currentIndex = batchEditReview?.index ?? 0
      const handlerOptions = inBatch ? { keepDiffOpen: true, skipSuccessToast: true } : undefined

      if (decision === 'accept') {
        await acceptFromChrome(edit, handlerOptions)
      } else {
        await rejectFromChrome(edit, handlerOptions)
      }

      if (!inBatch || !batchEditReview) return

      const nextIndex = currentIndex + 1
      if (nextIndex < batchEditReview.edits.length) {
        setBatchEditReview({ edits: batchEditReview.edits, index: nextIndex })
        setProposedEdit(batchEditReview.edits[nextIndex])
        return
      }

      closeBatchEditReview()
      if (decision === 'accept') {
        toast.success('All changes applied', {
          description: `${batchEditReview.edits.length} proposed edits reviewed`,
        })
      }
    },
    [acceptFromChrome, batchEditReview, closeBatchEditReview, rejectFromChrome, setProposedEdit],
  )

  const handleReviewProposedEdit = useCallback(
    (edit: ProposedEditData) => {
      openPendingEditDiff(edit)
    },
    [openPendingEditDiff],
  )

  const {
    handleNewChat: handleNewChatBase,
    handleShowHistory,
    handleSelectConversation,
    handleCloseConversation,
    handleArchiveConversation,
    handleExport,
    handleCopyTranscript,
    handleCopyConversationId,
    handleRenameConversation,
    handleRename: _handleRenameStub,
    handleClearHistory,
    handleSettings,
  } = conversationHandlers

  const resetChatSurface = useCallback(() => {
    clearHeldUserTurn(hostedThreadIdRef.current)
    committedUserTurnRef.current = null
    setTurnCommitted(false)
    handleNewChatBase()
  }, [handleNewChatBase])

  const handleRename = useCallback(async () => {
    const convId = streamingConversationId ?? currentConversationId
    if (convId) {
      const fromActive = activeConversations.find((c) => c.id === convId)?.title
      setRenameTarget({
        id: convId,
        title: fromActive || _hostedThreadTitle || 'New Chat',
      })
      setRenameDialogOpen(true)
      return
    }
    if (hostedThreadId && !hostedThreadId.startsWith('new-')) {
      try {
        const thread = await getThread(hostedThreadId)
        const linked = thread.linkedConversationId
        if (!linked) {
          toast.error('No conversation to rename yet')
          return
        }
        setRenameTarget({ id: linked, title: thread.title || _hostedThreadTitle || 'New Chat' })
        setRenameDialogOpen(true)
      } catch {
        toast.error('Failed to open rename')
      }
      return
    }
    toast.error('No conversation to rename')
  }, [
    streamingConversationId,
    currentConversationId,
    activeConversations,
    hostedThreadId,
    _hostedThreadTitle,
  ])

  const handleNewChat = useCallback(() => {
    const openDocContext = chatContextFromOpenDocument(
      activeDocumentIdRef.current,
      activeDocumentTitleRef.current,
      activeDocumentPathRef.current,
    )
    // Agent panel (+ button): registry owns thread binding; avoid hosted-thread reload loop.
    if (registry && surfaceVariant !== 'editor') {
      skipHostedThreadLoadRef.current = true
      // Switch thread FIRST so the hosted-thread load effect does not re-fetch the old
      // conversation after handleNewChatBase clears currentConversationId (race).
      registry.createNewChat({
        forceAgentPanel: true,
        ...openDocContext,
      })
      resetChatSurface()
      return
    }
    if (registry && surfaceVariant === 'editor') {
      registry.createNewChat({
        openInNewTab: true,
        ...openDocContext,
      })
      return
    }
    resetChatSurface()
  }, [
    registry,
    surfaceVariant,
    resetChatSurface,
    activeDocumentIdRef,
    activeDocumentTitleRef,
  ])

  /** Cmd+N (chat-create-new): registry bridge creates thread; compact pane must clear local state too. */
  useEffect(() => {
    if (surfaceVariant === 'editor') return
    const handler = () => {
      skipHostedThreadLoadRef.current = true
      resetChatSurface()
    }
    window.addEventListener('chat-create-new', handler)
    return () => window.removeEventListener('chat-create-new', handler)
  }, [surfaceVariant, resetChatSurface])

  const handleMoveToEditorTab = useCallback(() => {
    const openDocContext = chatContextFromOpenDocument(
      activeDocumentIdRef.current,
      activeDocumentTitleRef.current,
      activeDocumentPathRef.current,
    )
    let threadId = registry?.agentPanelThreadId ?? hostedThreadId ?? null

    if (!threadId && registry && surfaceVariant === 'compact') {
      threadId = registry.createNewChat({
        ...openDocContext,
      })
      if (threadId && streamingInput.trim()) {
        registry.setDraft(threadId, {
          ...(registry.getDraft(threadId)),
          text: streamingInput,
        })
      }
    }

    if (threadId) {
      if (registry) {
        registry.moveToEditorTab(threadId, { openInNewTab: true })
      } else {
        window.dispatchEvent(
          new CustomEvent('chat-surface-move', {
            detail: { threadId, target: 'editor' as const, openInNewTab: true },
          }),
        )
      }
      return
    }

    const conversationId =
      hostedLinkedConversationId ?? streamingConversationId ?? currentConversationId ?? undefined
    if (!conversationId) return

    void getThread(conversationId)
      .then((thread) => {
        if (registry) {
          registry.moveToEditorTab(thread.id, { openInNewTab: true })
        } else {
          window.dispatchEvent(
            new CustomEvent('chat-surface-move', {
              detail: { threadId: thread.id, target: 'editor' as const, openInNewTab: true },
            }),
          )
        }
      })
      .catch(() => {
        toast.error('Could not move chat to editor tab')
      })
  }, [
    registry,
    hostedThreadId,
    surfaceVariant,
    activeDocumentIdRef,
    activeDocumentTitleRef,
    streamingInput,
    hostedLinkedConversationId,
    currentConversationId,
    streamingConversationId,
  ])

  /** Delete hosted thread permanently, or archive legacy conversation-only chats. */
  const handleDeleteConversation = useCallback(async () => {
    const threadId = hostedThreadId
    if (threadId && !threadId.startsWith('new-')) {
      const convId = currentConversationId ?? streamingConversationId
      const title =
        activeConversations.find((c) => c.id === convId)?.title
        ?? _hostedThreadTitle
        ?? 'this chat'
      const confirmed = window.confirm(`Delete "${title}"? This cannot be undone.`)
      if (!confirmed) return
      try {
        await deleteThread(threadId)
        dismissChatThreadSurfaces(threadId)
        if (registry?.agentPanelThreadId === threadId) {
          registry.setAgentPanelThreadId(null)
        }
        refreshChatsSidebar()
        resetChatSurface()
        toast.success('Chat deleted')
      } catch (err) {
        console.error('[ChatPane] Delete thread failed:', err)
        toast.error('Failed to delete chat')
      }
      return
    }

    const convId = currentConversationId ?? streamingConversationId
    if (!convId) return
    const confirmed = window.confirm('Archive this conversation?')
    if (!confirmed) return
    await handleArchiveConversation(convId)
  }, [
    hostedThreadId,
    currentConversationId,
    streamingConversationId,
    activeConversations,
    _hostedThreadTitle,
    registry,
    resetChatSurface,
    handleArchiveConversation,
  ])

  const handleNewChatWithSkillReset = useCallback(() => {
    userExplicitlySetScope.current = false
    setActiveChatSkill(null)
    clearDismissedDocIdsForConversation(null)
    setDismissedDocIds(new Set())
    prevStreamingConvForDismissalsRef.current = null
    setDismissedModifiedFileKeys(new Set())
    setCompletedFiles([])
    setModifiedFilesPanelExpanded(false)
    handleNewChat()
  }, [handleNewChat, setCompletedFiles, setModifiedFilesPanelExpanded])

  const handleSelectConversationWithReset = useCallback(async (id: string) => {
    setDismissedModifiedFileKeys(new Set())
    setCompletedFiles([])
    setModifiedFilesPanelExpanded(false)
    return handleSelectConversation(id)
  }, [handleSelectConversation, setCompletedFiles, setModifiedFilesPanelExpanded])

  /** When a hosted thread is removed (agent panel cleared), reset to welcome state. */
  useEffect(() => {
    const prev = prevHostedThreadIdRef.current
    prevHostedThreadIdRef.current = hostedThreadId
    if (prev && !hostedThreadId) {
      // A workspace-mismatch release can abandon an in-flight load. That
      // load's finally bails when the thread id changed, so clear the spinner
      // here or the pane stays on "Loading conversation...".
      setLoadingConversation(false)
      resetChatSurface()
    }
  }, [hostedThreadId, resetChatSurface, setLoadingConversation])

  /** NEW-THREAD INIT: runs once per new-* thread ID. Separated from the "load existing conversation"
   * effect below so that streamingConversationId / handleNewChatBase dep changes during streaming
   * do NOT re-trigger handleNewChatBase() and wipe the in-progress stream. */
  const initializedNewThreadRef = useRef<string | null>(null)
  useEffect(() => {
    if (!hostedThreadId?.startsWith('new-')) {
      initializedNewThreadRef.current = null
      return
    }
    if (initializedNewThreadRef.current === hostedThreadId) return
    initializedNewThreadRef.current = hostedThreadId

    setLoadingConversation(false)
    resetChatSurface()
    // Restore any in-session draft the user typed before moving to this surface
    const newDraft = registryRef.current?.getDraft(hostedThreadId)
    if (newDraft?.text) setStreamingInput(newDraft.text)
    requestAnimationFrame(() => contentEditableInputRef.current?.focus())
  }, [hostedThreadId, resetChatSurface, setLoadingConversation, setStreamingInput])

  /** Hosted chat (editor tab or agent panel): load linked ai_conversation once thread metadata is resolved. */
  useEffect(() => {
    if (!hostedThreadId) return
    if (hostedThreadId.startsWith('new-')) {
      // Record the placeholder id so the promote switch sees previous = new-*,
      // not the prior saved thread. Otherwise the live user bubble is cleared.
      prevHostedLoadThreadRef.current = hostedThreadId
      skipHostedThreadLoadRef.current = false
      return // handled by effect above
    }
    const threadJustSwitched = prevHostedLoadThreadRef.current !== hostedThreadId
    if (threadJustSwitched) {
      pendingHostedLoadThreadRef.current = hostedThreadId
      // New Chat / Cmd+N sets skipHostedThreadLoadRef to avoid reloading the old conversation.
      // Explicit sidebar navigation to a persisted thread must always load that thread.
      if (!hostedThreadId.startsWith('new-')) {
        skipHostedThreadLoadRef.current = false
        failedHostedLoadConversationIdRef.current = null
        const keepLiveStream = shouldKeepLiveStreamOnHostedThreadSwitch({
          streamInFlight:
            streamInFlightRef.current || Boolean(abortControllerRef.current),
          hasOptimisticUserTurn: Boolean(committedUserTurnRef.current),
          previousHostedThreadId: prevHostedLoadThreadRef.current,
          streamingConversationId,
          hostedLinkedConversationId,
        })
        // Clear stale body + prior-thread draft immediately so title changes aren't paired
        // with an empty/wrong message list while the fetch is in flight.
        if (!keepLiveStream) {
          committedUserTurnRef.current = null
          setTurnCommitted(false)
          setStreamingMessages([])
          setPendingBasecampDraft(null)
          setPendingBasecampCreateDraft(null)
          const switchedDraft = registryRef.current?.getDraft(hostedThreadId)
          setStreamingInput(switchedDraft?.text ?? '')
        }
      }
      stopPollingRef.current()
      prevHostedLoadThreadRef.current = hostedThreadId
    }

    if (skipHostedThreadLoadRef.current) {
      return
    }

    if (hostedLinkedConversationId) {
      registryRef.current?.markChatStarted(hostedThreadId)
    }

    if (hostedThreadResolving || !hostedLinkedConversationId) {
      return
    }

    const activeConvId = currentConversationId ?? streamingConversationId
    // Linked conversation often resolves *after* the thread id binds (inbox hydrate).
    // That must force-load even when an "Ingesting: ..." chat is still current - otherwise
    // title/chip show the inbox thread while copy/export/feedback keep the ingest conversation.
    const linkedJustResolved =
      prevHostedLinkedConversationIdRef.current !== hostedLinkedConversationId
    prevHostedLinkedConversationIdRef.current = hostedLinkedConversationId
    const forceLoad =
      pendingHostedLoadThreadRef.current === hostedThreadId || linkedJustResolved

    // Continue / in-flight stream: do not replace live messages with a history snapshot
    // (that remount restored cappedAt and made Continue look unclicked).
    if (
      shouldSkipHostedHistoryReload({
        streamInFlight:
          streamInFlightRef.current || Boolean(abortControllerRef.current),
        streamingConversationId,
        hostedLinkedConversationId,
      })
    ) {
      if (pendingHostedLoadThreadRef.current === hostedThreadId) {
        pendingHostedLoadThreadRef.current = null
      }
      return
    }

    // forceLoad is set on threadJustSwitched / linkedJustResolved so history and inbox
    // opens always re-fetch even when a prior race left the same conversation id empty.
    if (!forceLoad && activeConvId === hostedLinkedConversationId) {
      return
    }

    // Known-gone guard: a prior attempt for this exact conversation already got a 404/410.
    // Without this, a failed load never updates activeConvId, so the check above never matches
    // and every dependency-driven re-run re-fires the same doomed fetch.
    // loadingConversation / isPolling / activeConversations are read via refs (not deps) so
    // their churn cannot restart a load mid-flight (2026-07-09 AbortError retry storm).
    if (!forceLoad && failedHostedLoadConversationIdRef.current === hostedLinkedConversationId) {
      return
    }

    // Ingestion guard: block automatic snap-back during active ingestion on the SAME surface.
    // Do not apply when the user switched threads or when linked_conversation_id just resolved
    // for the hosted thread (inbox Open-in-chat / hydrate).
    if (
      !forceLoad &&
      currentConversationId &&
      currentConversationId !== hostedLinkedConversationId
    ) {
      const conv = activeConversationsRef.current.find((c) => c.id === currentConversationId)
      const meta = (conv?.metadata ?? {}) as Record<string, unknown>
      const isIngestion =
        meta.type === 'basecamp-ingestion' || meta.type === 'ingestion'
      const titleIngesting = (conv?.title ?? '').trim().toLowerCase().startsWith('ingesting:')
      if (isIngestion || titleIngesting || isPollingRef.current || loadingConversationRef.current) {
        return
      }
    }

    const applySnapshot = (snap: ChatSessionSnapshot) => {
      setCurrentConversationId(null)
      setStreamingConversationId(snap.streamingConversationId ?? hostedLinkedConversationId)
      setStreamingMessages(snap.streamingMessages)
      setCompletedFiles(
        snap.completedFiles.map((f) => ({
          filePath: f.filePath,
          displayPath: f.displayPath,
          url: f.url,
          documentId: f.documentId,
          status: 'modified' as const,
          linesAdded: f.linesAdded ?? 0,
          linesRemoved: f.linesRemoved ?? 0,
        })),
      )
      setThinkingOperations(snap.thinkingOperations as ThinkingOperation[])
      setThinkingModeActive(snap.thinkingModeActive)
      setPendingBasecampDraft(snap.pendingBasecampDraft ?? null)
      setPendingBasecampCreateDraft(snap.pendingBasecampCreateDraft ?? null)
    }

    const snapshot = registryRef.current?.takeSessionSnapshot(hostedThreadId)
    if (
      snapshot &&
      (snapshot.streamingConversationId === hostedLinkedConversationId ||
        !snapshot.streamingConversationId)
    ) {
      applySnapshot(snapshot)
      const threadId = hostedThreadId
      const draft = registryRef.current?.getDraft(threadId)
      if (draft?.text) setStreamingInput(draft.text)
      if (pendingHostedLoadThreadRef.current === hostedThreadId) {
        pendingHostedLoadThreadRef.current = null
      }
      return
    }

    setLoadingConversation(true)
    const loadForThreadId = hostedThreadId
    const loadForConversationId = hostedLinkedConversationId
    void handleSelectConversationWithReset(hostedLinkedConversationId).then((result) => {
      if (result && !result.ok && (result.status === 404 || result.status === 410)) {
        failedHostedLoadConversationIdRef.current = loadForConversationId
      }
    }).finally(() => {
      if (hostedThreadIdRef.current !== loadForThreadId) {
        // Switched to another thread: that load owns the flag. Cleared to
        // Welcome (workspace release): nothing else will turn the spinner off.
        if (!hostedThreadIdRef.current) setLoadingConversation(false)
        return
      }
      setLoadingConversation(false)
      if (pendingHostedLoadThreadRef.current === loadForThreadId) {
        pendingHostedLoadThreadRef.current = null
      }
      // Restore any in-session draft typed before this surface was opened
      const threadId = hostedThreadIdRef.current
      if (threadId) {
        const draft = registryRef.current?.getDraft(threadId)
        if (draft?.text) setStreamingInput(draft.text)
      }
    })
  }, [
    hostedThreadId,
    hostedLinkedConversationId,
    hostedThreadResolving,
    currentConversationId,
    streamingConversationId,
    handleSelectConversationWithReset,
    setLoadingConversation,
    setStreamingInput,
    setStreamingMessages,
  ])

  /** Persist streaming input text to registry so surface moves preserve in-session typing.
   * Merge into existing draft — never wipe references/images (chip loss on re-focus).
   * Uses registryRef (not registry) intentionally: including registry in deps would create an infinite loop
   * because setDraft updates registry state -> new registry reference -> effect re-runs -> repeat. */
  useEffect(() => {
    if (!hostedThreadId || !registryRef.current) return
    const existing = registryRef.current.getDraft(hostedThreadId)
    registryRef.current.setDraft(hostedThreadId, {
      ...existing,
      text: streamingInput,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostedThreadId, streamingInput])

  /** Capture live chat state for surface moves (agent panel -> editor tab). */
  useEffect(() => {
    if (!hostedThreadId || !registry) return
    const threadId = hostedThreadId
    registry.setSessionExporter(threadId, () => ({
      streamingMessages,
      completedFiles: completedFiles.map((f) => ({
        filePath: f.filePath,
        displayPath: f.displayPath,
        url: f.url,
        documentId: f.documentId,
        title: f.title,
        linesAdded: f.linesAdded,
        linesRemoved: f.linesRemoved,
        changeStatus: f.changeStatus,
      })),
      thinkingOperations,
      streamingConversationId,
      thinkingModeActive,
      pendingBasecampDraft,
      pendingBasecampCreateDraft,
      capturedAt: Date.now(),
    }))
    return () => registry.clearSessionExporter(threadId)
  }, [
    hostedThreadId,
    registry,
    streamingMessages,
    completedFiles,
    thinkingOperations,
    streamingConversationId,
    thinkingModeActive,
    pendingBasecampDraft,
    pendingBasecampCreateDraft,
  ])

  /**
   * Agent panel welcome composer: bind typing to a draft thread (move menu).
   * If a real thread is already bound on the registry ref (hostedThreadId can lag
   * one frame), keep it. Do not reopen sessionStorage last-thread just because the
   * user typed on Welcome / New chat - that jumped people into Flow agent chats
   * (2026-09-01).
   */
  useEffect(() => {
    if (surfaceVariant !== 'compact' || collapsed || hostedThreadId || !registryRef.current) return
    if (!streamingInput.trim()) return

    const registryApi = registryRef.current
    const openDocContext = chatContextFromOpenDocument(
      activeDocumentIdRef.current,
      activeDocumentTitleRef.current,
      activeDocumentPathRef.current,
    )
    const draftPayload = {
      text: streamingInput,
      ...openDocContext,
    }

    const restoredId = registryApi.restoreLastAgentPanelThread('welcome-composer', {
      allowSwitch: false,
      activeWorkspaceIds,
    })
    if (registryApi.isAgentPanelRestoreLookupPending()) return
    if (restoredId && !restoredId.startsWith('new-')) {
      registryApi.setDraft(restoredId, {
        ...(registryApi.getDraft(restoredId)),
        ...draftPayload,
      })
      return
    }

    let threadId = registryApi.agentPanelThreadId ?? registryApi.getBlankNewChatId() ?? restoredId
    const boundToPanel =
      threadId && registryApi.getBinding(threadId)?.surface === 'agent-panel'

    if (!threadId || !boundToPanel) {
      threadId = registryApi.createNewChat({
        forceAgentPanel: true,
        ...openDocContext,
      })
    }

    registryApi.setDraft(threadId, {
      ...(registryApi.getDraft(threadId)),
      ...draftPayload,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- registryRef intentionally stable
  }, [surfaceVariant, collapsed, hostedThreadId, streamingInput, activeDocumentIdRef, activeDocumentTitleRef, activeWorkspaceIds])

  /** Focus composer when registry requests it for this hosted thread. */
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ threadId?: string }>).detail
      const targetThreadId = detail?.threadId
      if (targetThreadId && hostedThreadId && targetThreadId !== hostedThreadId) {
        // Registry may dispatch focus before React re-renders with the new hostedThreadId.
        if (!targetThreadId.startsWith('new-')) return
      }
      if (targetThreadId && !hostedThreadId && !targetThreadId.startsWith('new-')) return
      requestAnimationFrame(() => contentEditableInputRef.current?.focus())
    }
    window.addEventListener(focusChatComposerEventName(), handler)
    return () => window.removeEventListener(focusChatComposerEventName(), handler)
  }, [hostedThreadId, contentEditableInputRef])

  /** Editor tab: toolbar menu actions from document-viewer ChatConversationToolbar. */
  useEffect(() => {
    if (surfaceVariant !== 'editor' || !hostedThreadId) return

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ threadId?: string; action?: ChatToolbarAction }>).detail
      if (!detail || detail.threadId !== hostedThreadId || !detail.action) return

      switch (detail.action) {
        case 'export':
          handleExport()
          break
        case 'copy-transcript':
          void handleCopyTranscript()
          break
        case 'rename':
          handleRename()
          break
        case 'delete':
          void handleDeleteConversation()
          break
        case 'clear-history':
          handleClearHistory()
          break
        case 'settings':
          handleSettings()
          break
        case 'learn-from-session': {
          const nudge =
            'Review this conversation for durable preferences or project facts worth saving to my `.soul/` (especially `memory.md`). For each, call proposeSoulUpdate with an exact patch (trigger: learn_from_session). If nothing durable, say so - do not invent memories.'
          void handleSendRef.current(undefined, nudge)
          break
        }
        default:
          break
      }
    }

    window.addEventListener(chatToolbarActionEventName(), handler)
    return () => window.removeEventListener(chatToolbarActionEventName(), handler)
  }, [
    surfaceVariant,
    hostedThreadId,
    handleExport,
    handleCopyTranscript,
    handleRename,
    handleDeleteConversation,
    handleClearHistory,
    handleSettings,
  ])

  /** Open a session completed file after verifying it exists (by-path first, then UUID). */
  const openCompletedFile = useCallback(
    async (file: { filePath: string; documentId?: string }) => {
      const openId = (id: string) => {
        onDocumentClick?.(id)
      }

      if (file.filePath) {
        try {
          const response = await fetchWithAuth(
            `/api/documents/by-path?path=${encodeURIComponent(file.filePath)}`,
          )
          if (response.ok) {
            const doc = await response.json()
            openId(doc.id)
            return
          }
          if (response.status === 403) {
            toast.error('No access to this document', { description: file.filePath })
            return
          }
        } catch {
          // fall through to UUID lookup
        }
      }

      if (file.documentId) {
        try {
          const response = await fetchWithAuth(`/api/documents/${file.documentId}`)
          if (response.ok) {
            openId(file.documentId)
            return
          }
        } catch {
          // handled below
        }
      }

      toast.error('File not found', {
        description:
          'Document may have been undone, deleted, or is outside your workspace org. Search by filename or recreate.',
      })
      setCompletedFiles((prev) =>
        prev.filter((f) => f.filePath !== file.filePath && f.documentId !== file.documentId),
      )
    },
    [onDocumentClick, setCompletedFiles],
  )

  /**
   * Files bar Review: open pending proposed_edit diffs when present. createDocument /
   * direct writes only land in completedFiles (no original/proposed) - do not silently
   * no-op; expand list and open the first file with an explanation toast.
   */
  const handleReviewModifiedFiles = useCallback(() => {
    if (openPendingEditDiff()) return
    setModifiedFilesPanelExpanded(true)
    const first =
      completedFiles[0] ||
      modifiedFilesForPanel.find((r) => r.documentId || r.filePath)
    if (first) {
      toast.info('No pending diffs to review', {
        description:
          'These files were already written (e.g. createDocument). Click a row to open, or Undo to remove.',
      })
      void openCompletedFile({
        filePath: first.filePath,
        documentId: first.documentId,
      })
      return
    }
    toast.info('Nothing to review', {
      description: 'No pending edits or completed files in this chat session.',
    })
  }, [
    openPendingEditDiff,
    setModifiedFilesPanelExpanded,
    completedFiles,
    modifiedFilesForPanel,
    openCompletedFile,
  ])

  // Use custom hooks for conversation loading and event handling
  useConversationHistoryLoader({
    setStreamingMessages,
    setStreamingConversationId,
    lastLoadedScopePathsRef,
    skip: Boolean(hostedThreadId),
  })

  useWorkspaceScopeApplier({
    workspaces,
    setConversationScope,
    lastLoadedScopePathsRef,
    userExplicitlySetScopeRef: userExplicitlySetScope,
  })

  useConversationIdPersistence(streamingConversationId)

  useOpenConversationListener({
    collapsed,
    onToggleCollapse,
    setLoadingConversation,
    setLegacyMessages,
    setStreamingMessages,
    setCurrentConversationId,
    setStreamingConversationId,
    setThinkingModeActive,
    setThinkingOperations,
    setCompletedFiles,
    setCurrentTask,
    startPolling,
    stopPolling,
    messagesContainerRef,
    surfaceVariant,
    hostedThreadId,
    onIngestionBootstrapComplete: registry?.endIngestionBootstrap,
  })

  useChatEventListeners({
    collapsed,
    onToggleCollapse,
    contentEditableInputRef,
    setStreamingInput,
    setCurrentConversationId,
    setLegacyMessages,
    setStreamingMessages,
  })

  useBasecampNotesListeners({
    hostedThreadId,
    collapsed,
    onToggleCollapse,
    setThinkingModeActive,
    setThinkingOperations,
    setCompletedFiles,
    setCurrentTask,
    setPendingBasecampDraft,
    messagesContainerRef,
  })

  useTriageAnalyzeListener({
    collapsed,
    onToggleCollapse,
    setCurrentConversationId,
    setStreamingMessages,
    setThinkingModeActive,
    setThinkingOperations,
    setIsStreaming,
    setIsTyping,
    selectedProvider,
    selectedModel,
    workspaceScopePaths,
    abortControllerRef,
  })

  useTodoActionChatListener({
    hostedThreadId,
    collapsed,
    onToggleCollapse,
    setStreamingMessages,
    setThinkingModeActive,
    setThinkingOperations,
    setIsStreaming,
    setIsTyping,
    selectedProvider,
    selectedModel,
    workspaceScopePaths,
    abortControllerRef,
  })

  // Keep a window helper for image-search insert (ToolMessage may not remount on tab change)
  useEffect(() => {
    const syncActiveDoc = () => {
      try {
        ;(window as unknown as { __flownotesActiveDocumentId?: string | null }).__flownotesActiveDocumentId =
          activeDocumentIdRef.current || null
      } catch {
        /* ignore */
      }
    }
    syncActiveDoc()
    const id = window.setInterval(syncActiveDoc, 1000)
    return () => window.clearInterval(id)
  }, [activeDocumentIdRef])

  // Listen for test-proposed-edit + flownotes:propose-document-edit (image search insert)
  useEffect(() => {
    const handleProposedEditEvent = (event: CustomEvent) => {
      const proposedEditData = event.detail;
      const isTest = event.type === 'test-proposed-edit'
      devLog('[ChatPane] Proposed edit event received:', event.type, proposedEditData);

      // Process the proposed edit the same way as SSE events
      if (proposedEditData.type === 'proposed_edit') {
        const documentId = proposedEditData.documentId || activeDocumentIdRef.current;

        if (!documentId) {
          console.error('[ChatPane] Proposed edit received but no documentId available', proposedEditData);
          toast.error('Open a document before inserting')
          return;
        }

        const changedLinesPayload = normalizeChangedLines(proposedEditData.changedLines ?? proposedEditData.changed_lines);
        let additions = changedLinesPayload ? changedLinesPayload.added.length : 0;
        let deletions = changedLinesPayload ? changedLinesPayload.removed.length : 0;

        if (!changedLinesPayload) {
          // Compute diff if not provided
          import('diff').then(({ diffLines }) => {
            const changes = diffLines(proposedEditData.original || '', proposedEditData.proposed || '');
            changes.forEach(change => {
              if (change.added) additions += change.count || 0;
              if (change.removed) deletions += change.count || 0;
            });
          });
        }

        const editData: ProposedEditData = {
          documentId: documentId,
          filename: proposedEditData.filename || 'document.md',
          title: proposedEditData.title || 'Document',
          original: proposedEditData.original || '',
          proposed: proposedEditData.proposed || '',
          frontmatter: proposedEditData.frontmatter,
          explanation: proposedEditData.explanation,
          status: 'pending',
          additions,
          deletions,
          changedLines: changedLinesPayload,
          changeStatus: proposedEditData.changeStatus || 'pending',
          requiresAcceptance: proposedEditData.requiresAcceptance ?? true,
          conversationId: streamingConversationId,
        };

        setProposedEdit(editData);
        // Don't auto-open modal - user can accept directly from chat widget
        // setDiffPreviewOpen(true);

        toast.info(isTest ? 'Test edit triggered' : 'Edit proposed', {
          description: `${editData.filename}: review and accept to apply`,
          duration: 5000
        });
      }
    };

    window.addEventListener('test-proposed-edit', handleProposedEditEvent as unknown as EventListener);
    window.addEventListener('flownotes:propose-document-edit', handleProposedEditEvent as unknown as EventListener);

    return () => {
      window.removeEventListener('test-proposed-edit', handleProposedEditEvent as unknown as EventListener);
      window.removeEventListener('flownotes:propose-document-edit', handleProposedEditEvent as unknown as EventListener);
    };
  }, [activeDocumentIdRef, streamingConversationId, setProposedEdit]);

  // Use handlers from hooks (simple handlers are already extracted)
  // Keep queued messages in sync with a ref so the finally block can read the latest value
  // without stale closure issues
  const queuedMessagesRef = useRef<QueuedChatMessage[]>(queuedMessages)
  useEffect(() => { queuedMessagesRef.current = queuedMessages }, [queuedMessages])

  const syncQueueEdit = (next: QueueEditSession | null) => {
    queueEditRef.current = next
    setQueueEdit(next)
  }

  const commitQueue = (next: QueuedChatMessage[]) => {
    queuedMessagesRef.current = next
    setQueuedMessages(next)
  }

  const restoreComposerDraft = (session: QueueEditSession) => {
    contentEditableInputRef.current?.setPlainText(session.stashedText)
    contentEditableInputRef.current?.setImages(session.stashedImages)
    onClearReferences?.()
    for (const ref of session.stashedReferences) {
      onAddReference?.(ref)
    }
    if (session.stashedSkill) {
      contentEditableInputRef.current?.activateSkill(session.stashedSkill)
    } else {
      setActiveChatSkill(null)
    }
    syncQueueEdit(null)
    requestAnimationFrame(() => contentEditableInputRef.current?.focus())
  }

  const beginQueueEdit = (item: QueuedChatMessage) => {
    const composerText =
      contentEditableInputRef.current?.getSerializedMessageText?.() ?? streamingInput
    const draftText =
      contentEditableInputRef.current?.getDraftPlainText?.() ?? streamingInput
    const opened = openQueueEditSession({
      item,
      session: queueEditRef.current,
      composerText,
      draftText,
      draftImages: contentEditableInputRef.current?.getImages?.() ?? [],
      draftReferences: references,
      draftSkill: activeChatSkill,
    })
    if (!opened) return
    if (opened.focusOnly) {
      contentEditableInputRef.current?.focus()
      return
    }
    if (opened.commit) {
      commitQueue(
        applyQueueTextUpdate(queuedMessagesRef.current, opened.commit.id, opened.commit.text),
      )
    }
    syncQueueEdit(opened.session)
    contentEditableInputRef.current?.setPlainText(opened.composerText)
    contentEditableInputRef.current?.setImages([])
    onClearReferences?.()
    setQueuePanelExpanded(true)
    requestAnimationFrame(() => contentEditableInputRef.current?.focus())
  }

  const finishQueueEdit = (mode: 'save' | 'cancel') => {
    const session = queueEditRef.current
    if (!session) return
    const composerText =
      contentEditableInputRef.current?.getSerializedMessageText?.() ?? streamingInput
    const row = queuedMessagesRef.current.find((entry) => entry.id === session.id)
    const result = closeQueueEditSession({
      session,
      mode,
      composerText,
      rowHasImages: (row?.images.length ?? 0) > 0,
    })
    if (!result.closed) return
    if (result.savedText !== null && row) {
      commitQueue(applyQueueTextUpdate(queuedMessagesRef.current, session.id, result.savedText))
    }
    restoreComposerDraft(session)
  }

  const removeQueuedRow = (item: QueuedChatMessage, index: number) => {
    const session = queueEditRef.current
    const next = item.id
      ? removeQueuedById(queuedMessagesRef.current, item.id)
      : queuedMessagesRef.current.filter((_, idx) => idx !== index)
    commitQueue(next)
    if (session && item.id && session.id === item.id) {
      restoreComposerDraft(session)
    }
  }
  /** True while draining the queue so contentOverride sends are not re-queued. */
  const flushQueuedSendRef = useRef(false)

  /** Set synchronously in onSaveEdit so handleSend uses the sliced thread (closure streamingMessages is stale). */
  const editRepromptBaseRef = useRef<Message[] | null>(null)
  /** One-shot viewContext from Generate todo notes (unsaved form snapshot). */
  const viewContextOverrideRef = useRef<ViewContext | null>(null)

  // contentOverride: when flushing a queued message, pass the text directly instead of reading from streamingInput
  const handleSend = async (
    e?: React.FormEvent,
    contentOverride?: string,
    sendExtras?: { images?: ChatImage[] },
  ) => {
    e?.preventDefault();

    const streamingNow = isStreamingRef.current
    // sendBusy stays true for the whole stream. Queue must still run.
    if (sendBusyRef.current && !streamingNow) return

    if (currentConversationId) {
      toast.info('Ingestion in progress', {
        description: 'Wait until the ingestion job finishes before sending messages.',
      })
      return
    }

    const requestViewContext = viewContextOverrideRef.current ?? viewContext
    viewContextOverrideRef.current = null

    const repromptMessages = editRepromptBaseRef.current
    const isAgenticContinue = contentOverride === 'continue'

      const domSerialized =
        isAgenticContinue || typeof contentOverride === 'string'
          ? undefined
          : contentEditableInputRef.current?.getSerializedMessageText?.()

      const streamingFallback =
        typeof domSerialized === 'string' ? domSerialized : streamingInput

      const rawInput =
        isAgenticContinue
          ? ''
          : typeof contentOverride === 'string'
            ? contentOverride
            : streamingFallback

      const messageText = rawInput.trim()

      // Enter while a queued row is open saves that text. It does not queue or send.
      if (
        queueEditRef.current &&
        !flushQueuedSendRef.current &&
        !isAgenticContinue &&
        typeof contentOverride !== 'string'
      ) {
        finishQueueEdit('save')
        return
      }

      // If the user is asking where the proposed changes are and edits are pending,
      // proactively open the latest diff (Alice incident: "where do I see your recent changes?").
      const askedWhereChanges =
        /\b(where|cant find|can'?t find|not seeing|don'?t see|do i see|double[- ]?check).{0,40}\b(change|edit|diff|update)/i.test(
          messageText,
        )
      if (askedWhereChanges && pendingProposedEdits.length > 0) {
        openPendingEditDiff()
        toast.info('Here are your pending changes', {
          description: 'Review and accept or reject the proposed edit.',
        })
      }

      // If a stream is already in flight, queue (including action-choice contentOverride).
      // Previously contentOverride bypassed the queue and started a second concurrent stream
      // (dual "Working on it..." after Force apply / Use patch buttons).
      if (streamingNow && !flushQueuedSendRef.current && !isAgenticContinue) {
        const queuedImages = contentOverride
          ? []
          : (contentEditableInputRef.current?.getImages?.() ?? [])
        const queued = captureComposerQueueItem({ text: rawInput, images: queuedImages })
        if (queued) {
          // Mid-flash-card chat = pause run (discuss this), even when queued
          pauseOnUserChat()
          setQueuedMessages(prev => [...prev, queued])
          if (!contentOverride) {
            setStreamingInput('')
            contentEditableInputRef.current?.clear?.()
            contentEditableInputRef.current?.clearImages?.()
          }
        }
        return
      }

      sendBusyRef.current = true
      setSendBusy(true)
      try {

      const stagedImages = resolveStagedImagesForSend({
        imagesOverride: sendExtras?.images,
        composerImages: contentEditableInputRef.current?.getImages?.() ?? [],
        skipComposer: repromptMessages != null,
      })

      if (stagedImages.length > 0) {
        const imageCheck = validateStagedImagesOnly(stagedImages)
        if (!imageCheck.ok) {
          toast.error(CHAT_IMAGE_BUDGET_TOAST_TITLE, {
            description: imageCheck.message,
            duration: 8000,
          })
          return
        }
      }

      let imagesForSend = stagedImages
      if (stagedImages.length > 0) {
        imagesForSend = await stageChatImagesForSend(stagedImages, {
          threadId: hostedThreadId && !hostedThreadId.startsWith('new-') ? hostedThreadId : null,
          conversationId: streamingConversationId || hostedLinkedConversationId || null,
          organizationId: organizationIdForChatImageUpload({
            resolvedScopeIds,
            openDocumentPath: activeDocumentPathRef.current,
            workspaces,
          }),
        })
      }

      // On the first message of a new chat, inject the document context chip as a reference.
      // Session scratch stays in Session files - do not auto-chip it on the prompt.
      const ctxDocId = contextDocumentIdRef.current
      const ctxDocTitle = contextDocumentTitleRef.current
      const contextIsSessionScratch =
        isSessionScratchDisplayTitle(ctxDocTitle) ||
        (Boolean(ctxDocId) &&
          ctxDocId === activeDocumentIdRef.current &&
          isSessionScratchPath(activeDocumentPathRef.current))
      const contextDocRef: import('@/components/layout/chat-pane.types').DocumentReference | null =
        ctxDocId && !contextIsSessionScratch && !references.some((r) => r.id === ctxDocId)
          ? { id: ctxDocId, title: ctxDocTitle ?? ctxDocId, type: 'note', kind: 'document' }
          : null
      const effectiveReferences = contextDocRef ? [...references, contextDocRef] : references
      const bubbleReferences = visibleMessageReferences(effectiveReferences)

      if (!isAgenticContinue && !rawInput.trim() && effectiveReferences.length === 0 && stagedImages.length === 0) return

      // User message mid flash-card run = pause for discussion (Resume / Dismiss CTA)
      if (!isAgenticContinue) {
        pauseOnUserChat()
      }

      // Clear the chip after use so it doesn't appear on subsequent messages
      if (contextDocRef && hostedThreadId) {
        const currentDraft = registry?.getDraft(hostedThreadId)
        if (currentDraft) {
          registry?.setDraft(hostedThreadId, { ...currentDraft, contextDocumentId: undefined, contextDocumentTitle: undefined })
        }
      }

      if (hostedThreadId?.startsWith('new-')) {
        registry?.markChatStarted(hostedThreadId)
      }

      // When user sends chips only (no typed text), synthesize a message from the reference titles
      const messageContent =
        rawInput.trim() ||
        (effectiveReferences.length > 0
          ? effectiveReferences.map(r => r.title || r.id).join(', ')
          : '') ||
        (stagedImages.length > 0 ? '(image)' : '')

      let userMessage: Message | null = null
      /** Conversation prefix + new/edited user turn for API and post-send checks (not stale closure). */
      let messagesForHistory: Message[]
      /** Edit / regenerate / stop-reprompt already persisted the user row - do not INSERT again. */
      let skipPersistUserMessage = false

      // Queue flush re-enters this function from the send that just finished.
      // That closure's streamingMessages snapshot omits the turn it just painted.
      // Replacing the pane with snapshot + the new user drops that turn until reload.
      // streamingMessagesRef is assigned on every render, including the final reply.
      const liveThread = streamingMessagesRef.current

      if (isAgenticContinue) {
        messagesForHistory = [...liveThread]
      } else if (repromptMessages) {
        const tail = repromptMessages[repromptMessages.length - 1]
        if (!tail || tail.role !== 'user') {
          console.warn('[ChatPane] editReprompt snapshot missing user tail; aborting send')
          return
        }
        editRepromptBaseRef.current = null
        userMessage = tail
        messagesForHistory = repromptMessages
        skipPersistUserMessage = true
      } else {
        userMessage = {
          role: 'user',
          content: messageContent,
          references: bubbleReferences.length > 0 ? [...bubbleReferences] : undefined,
          images: imagesForSend.length > 0 ? imagesForSend : undefined,
          skill: activeChatSkill ?? undefined,
          timestamp: new Date()
        }
        messagesForHistory = [...liveThread, userMessage]
      }

      /** Snapshot before parent clears composer refs (survives async gaps after await). */
      let referencesForRequest = [...effectiveReferences]
      if (repromptMessages != null) {
        const tailMsg = repromptMessages[repromptMessages.length - 1]
        if (tailMsg?.references?.length) {
          referencesForRequest = [...tailMsg.references]
        }
      }

      const payloadCheck = validateChatStreamPayload(
        buildChatStreamRequestBody({
          messagesForHistory,
          referencesForRequest,
          activeDocumentIdRef,
          dismissedDocIdsRef,
          viewContext: requestViewContext,
          workspaceScopePaths,
          selectedProvider,
          selectedModel,
          chatMode: chatModeRef.current,
          activeChatSkill,
          agenticContinue: isAgenticContinue,
          savedConvId: streamingConversationId ?? hostedLinkedConversationId,
          streamingConversationId,
          hostedLinkedConversationId,
        }),
      )
      if (!payloadCheck.ok) {
        toast.error(CHAT_PAYLOAD_TOAST_TITLE, {
          description: payloadCheck.message,
          duration: 8000,
        })
        return
      }

      if (!repromptMessages && !isAgenticContinue) {
        setStreamingMessages(messagesForHistory)
      }
      if (userMessage && !isAgenticContinue) {
        committedUserTurnRef.current = userMessage
        setTurnCommitted(true)
        rememberHeldUserTurn(hostedThreadIdRef.current, userMessage)
      }

      // Show pulsing dots before POST /conversation (new chats wait on that round-trip).
      // Otherwise the user bubble sits in a blank pane until SSE thinking_start.
      setIsStreaming(true)
      setIsTyping(true)
      // Before persist: POST /conversation ensureAgentChatThread can promote new-*
      // while AbortController still does not exist.
      streamInFlightRef.current = true

      if (!contentOverride) {
        setStreamingInput("")  // Clear input state
        contentEditableInputRef.current?.clear?.()  // Clear DOM immediately (avoids race with input events)
        contentEditableInputRef.current?.clearImages?.()  // Pasted images are separate from editor text
        onClearReferences?.()
      } else if (isAgenticContinue) {
        onClearReferences?.()
      }

      // Keep the question at the top of the viewport. Scrolling to the bottom
      // hides it once the thread is taller than the pane.
      userHasScrolledRef.current = false
      setUserHasScrolled(false)
      setIsNearBottom(true)
      const lastUserIndex = (() => {
        for (let i = messagesForHistory.length - 1; i >= 0; i -= 1) {
          if (messagesForHistory[i]?.role === 'user') return i
        }
        return -1
      })()
      setTimeout(() => {
        const el = messagesContainerRef.current
        if (!el || lastUserIndex < 0) return
        isProgrammaticScrollRef.current = true
        scrollLatestUserTurnToTop(el, lastUserIndex)
        requestAnimationFrame(() => {
          isProgrammaticScrollRef.current = false
        })
      }, 50)

      const priorUserMessageCount = repromptMessages
        ? repromptMessages.filter((m) => m.role === 'user').length - 1
        : streamingMessages.filter((m) => m.role === 'user').length
      const isFirstUserMessageInChat = priorUserMessageCount === 0

      const scopePathsForPersist =
        (requestViewContext?.type === 'triage' ||
          requestViewContext?.type === 'basecamp-todo' ||
          requestViewContext?.type === 'basecamp-project' ||
          requestViewContext?.type === 'basecamp-activity') &&
        workspaceScopePaths.length > 0
          ? workspaceScopePaths
          : workspaceScopePathsForPersist(
              conversationScope,
              activeWorkspaceIds,
              workspaces,
            )

      // Save user message to history IMMEDIATELY (before streaming starts)
      // Skip for agentic Continue - no user row; backend resumes from conversationId
      let savedConvId: string | null = streamingConversationId
      let savedUserMsgId: string | null = null
      let streamResult: { cappedAt?: 'max_rounds' | 'max_tools' | 'incomplete_write' } | undefined

      try {
        if (!isAgenticContinue && userMessage && !skipPersistUserMessage) {
        const persistedAttachmentIds = [
          ...new Set(
            (userMessage.images || [])
              .map((img) => img.attachmentId)
              .filter((id): id is string => Boolean(id)),
          ),
        ]
        const saveResult = await saveMessageToHistory(
          'user',
          userMessage.content,
          streamingConversationId ?? hostedLinkedConversationId ?? undefined,
          {
            ...(isPlanBuildUserText(userMessage.content) && { harness: 'plan_build' }),
            ...(activeChatSkill?.id && {
              skillId: activeChatSkill.id,
              skillName: activeChatSkill.name,
            }),
            ...(scopePathsForPersist !== undefined && { workspaceScope: scopePathsForPersist }),
            // Always send live chat-context roots for tag union, even when persist is NULL
            // ("follow all active"). Prevents JWT-only under-tagging on the save path.
            ...(workspaceScopePaths.length > 0 && {
              workspaceScopeForTags: workspaceScopePaths,
            }),
            ...(persistedAttachmentIds.length > 0 && {
              metadata: { attachmentIds: persistedAttachmentIds },
            }),
          },
        )
        savedConvId = saveResult.conversationId
        savedUserMsgId = saveResult.messageId
      } else if (userMessage?.messageId) {
        savedUserMsgId = userMessage.messageId
      }

      if (
        !isAgenticContinue &&
        isFirstUserMessageInChat &&
        conversationScope != null &&
        draftScopeDiffersFromActive(conversationScope, activeWorkspaceIds)
      ) {
        showApplyScopeToAppToast(
          conversationScope,
          setActiveWorkspaceIds,
          activeWorkspaceIds,
        )
      }

      // Store DB message ID on the user message so edit-prompt can reference it
      if (savedUserMsgId) {
        setStreamingMessages(prev => {
          const msgs = [...prev]
          const lastUser = [...msgs].reverse().find(m => m.role === 'user' && !m.messageId)
          if (lastUser) lastUser.messageId = savedUserMsgId
          return msgs
        })
      }

        if (!streamInFlightRef.current) {
          setIsStreaming(false)
          setIsTyping(false)
          return
        }

        // Create abort controller for cancellation
        const abortController = new AbortController()
        abortControllerRef.current = abortController
        streamResult = await executeGeneralChatStream({
          abortController,
          abortControllerRef,
          streamEpochRef,
          savedConvId,
          messagesForHistory,
          referencesForRequest,
          activeDocumentIdRef,
          dismissedDocIdsRef,
          viewContext: requestViewContext,
          workspaceScopePaths,
          selectedProvider,
          selectedModel,
          chatMode: chatModeRef.current,
          activeChatSkill,
          streamingConversationId,
          scopedAvailableSkills,
          setStreamingMessages,
          setIsStreaming,
          setIsTyping,
          setThinkingModeActive,
          setThinkingOperations,
          setCurrentTask,
          setCompletedFiles,
          setAgenticRound,
          setProposedEdit,
          onStreamProposedEditsComplete: handleStreamProposedEditsComplete,
          setPendingBasecampDraft,
          setPendingBasecampCreateDraft,
          setActiveChatSkill,
          messagesContainerRef,
          userHasScrolledRef,
          saveMessageToHistory,
          activeConversations,
          setActiveConversations,
          setHistoryRefreshKey,
          isTypingRef,
          hostedThreadIdRef,
          claimAgentPanelOnNewThread:
            surfaceVariant === 'compact' &&
            !streamingConversationId &&
            !hostedLinkedConversationId,
          onHostedThreadTitleSynced: (title) => {
            const threadId = hostedThreadIdRef.current;
            if (threadId) {
              registryRef.current?.registerThreadTitle(threadId, title);
            }
          },
          agenticContinue: isAgenticContinue,
          onContextUsage: setContextUsage,
          hostedLinkedConversationId,
        })
      } finally {
        streamInFlightRef.current = false
        
        const decision = decideQueueFlush({
          sendNowPending: sendNowPendingRef.current,
          userStopped: userStoppedRef.current,
          queue: queuedMessagesRef.current,
          isAgenticContinue,
          streamCappedAt: streamResult?.cappedAt,
          editingQueueId: queueEditRef.current?.id ?? null,
        })

        // Clear refs after decision
        if (decision.type === 'send-now' || decision.type === 'user-stopped') {
          sendNowPendingRef.current = null
          userStoppedRef.current = false
        }

        // Execute the decision
        if (decision.type === 'send-now') {
          setTimeout(() => {
            flushQueuedSendRef.current = true
            void Promise.resolve(handleSendRef.current(undefined, decision.item.text, { images: decision.item.images })).finally(() => {
              flushQueuedSendRef.current = false
            })
          }, 50)
        } else if (decision.type === 'natural-flush') {
          const [, ...rest] = queuedMessagesRef.current
          setQueuedMessages(rest)
          setTimeout(() => {
            flushQueuedSendRef.current = true
            void Promise.resolve(handleSendRef.current(undefined, decision.item.text, { images: decision.item.images })).finally(() => {
              flushQueuedSendRef.current = false
            })
          }, 50)
        }
        // user-stopped and no-flush: do nothing
      }
      } finally {
        sendBusyRef.current = false
        setSendBusy(false)
      }
  }

  /** Send now: abort current turn and send this specific queued message. */
  const handleSendNow = (item: QueuedChatMessage, index: number) => {
    const session = queueEditRef.current
    let payload = item
    if (session && item.id && session.id === item.id) {
      const live = (contentEditableInputRef.current?.getSerializedMessageText?.() ?? '').trim()
      if (!live && item.images.length === 0) return
      payload = { ...item, text: live }
      restoreComposerDraft(session)
    }
    const next = item.id
      ? removeQueuedById(queuedMessagesRef.current, item.id)
      : queuedMessagesRef.current.filter((_, idx) => idx !== index)
    commitQueue(next)
    sendNowPendingRef.current = payload
    if (isStreaming) {
      streamInFlightRef.current = false
      handleStopBase()
    } else {
      flushQueuedSendRef.current = true
      void Promise.resolve(handleSendRef.current(undefined, payload.text, { images: payload.images })).finally(() => {
        flushQueuedSendRef.current = false
        sendNowPendingRef.current = null
      })
    }
  }

  // Listen for inbox reply auto-send events (dispatched by InboxReader after opening conversation)
  const handleSendRef = useRef(handleSend)
  handleSendRef.current = handleSend

  const tryFlushUpdateWorkSpecKickoffRef = useRef(() => {})
  tryFlushUpdateWorkSpecKickoffRef.current = () => {
    const detail = consumeUpdateWorkSpecKickoffIfReady({
      hostedThreadId: hostedThreadIdRef.current,
      conversationId:
        streamingConversationIdRef.current ??
        hostedLinkedConversationIdRef.current ??
        currentConversationIdRef.current ??
        null,
      messages: streamingMessagesRef.current,
      isStreaming: isStreamingRef.current,
    })
    if (!detail) return
    if (detail.documentId) {
      activeDocumentIdRef.current = detail.documentId
    }
    if (detail.skillId) {
      const skillMeta = scopedAvailableSkills.find((s) => s.id === detail.skillId)
      setActiveChatSkill(
        skillMeta
          ? { id: skillMeta.id, name: skillMeta.name }
          : { id: detail.skillId, name: detail.skillId },
      )
    }
    void handleSendRef.current(undefined, 'continue')
  }

  useEffect(() => {
    const flush = () => tryFlushUpdateWorkSpecKickoffRef.current()
    const compatContinue = (e: Event) => {
      const detail = (e as CustomEvent<{
        targetThreadId?: string
        targetConversationId?: string
        documentId?: string
        skillId?: string
      }>).detail ?? {}
      if (!detail.targetConversationId) return
      enqueueUpdateWorkSpecKickoff(detail)
    }
    window.addEventListener(UPDATE_WORK_SPEC_KICKOFF_EVENT, flush)
    window.addEventListener('chat-agentic-continue', compatContinue)
    return () => {
      window.removeEventListener(UPDATE_WORK_SPEC_KICKOFF_EVENT, flush)
      window.removeEventListener('chat-agentic-continue', compatContinue)
    }
  }, [])

  useEffect(() => {
    tryFlushUpdateWorkSpecKickoffRef.current()
  }, [
    hostedThreadId,
    streamingConversationId,
    hostedLinkedConversationId,
    currentConversationId,
    streamingMessages,
    isStreaming,
  ])

  /** Leave Plan mode and ask Agent to create the drafted files / implement the plan. */
  const handleBuildFromPlan = useCallback(() => {
    chatModeRef.current = 'agent'
    setChatMode('agent')
    void handleSendRef.current(undefined, PLAN_BUILD_USER_TEXT)
  }, [])

  const handleActionChoiceClick = useCallback(
    (choice: ActionChoice, message: Message, messageIndex: number) => {
      const clearChoices = (setter: typeof setStreamingMessages) =>
        setter((prev) =>
          prev.map((m, idx) => (idx === messageIndex ? { ...m, actionChoices: undefined } : m)),
        )
      if (currentConversationId) {
        setLegacyMessages((prev) =>
          prev.map((m, idx) => (idx === messageIndex ? { ...m, actionChoices: undefined } : m)),
        )
      } else {
        clearChoices(setStreamingMessages)
      }

      const target = resolveActionChoiceTarget(
        choice,
        buildMessageDocumentsMap(message),
      )
      if (target.type === 'fill_comment_form') {
        const session = getActiveTodoActionSession()
        const text = extractPlainCommentDraft(target.text || message.content || '')
        if (!text) {
          toast.error('No comment body yet', {
            description: 'Answer the shaping questions, then try again.',
          })
          return
        }
        const todoId = session?.todoId
        if (todoId) {
          window.dispatchEvent(
            new CustomEvent('basecamp-todo-promote-comments', { detail: { todoId } }),
          )
        }
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('basecamp-comment-form-fill', {
              detail: { todoId, text },
            }),
          )
        }, 50)
        toast.success('Comment draft added to form', {
          description: 'Review in Comments, then post when ready.',
        })
        return
      }
      if (target.type === 'open_document') {
        const openId = target.documentId ?? target.filePath
        if (openId && onDocumentClick) {
          void onDocumentClick(openId)
          return
        }
        toast.error('Cannot open document', {
          description: 'Document reference is missing from this action.',
        })
        return
      }
      setTimeout(() => void handleSendRef.current(undefined, target.text), 0)
    },
    [currentConversationId, onDocumentClick, setLegacyMessages, setStreamingMessages],
  )

  const handleAskQuestionSubmit = useCallback(
    async (
      ask: import('./chat-pane.types').AskQuestionData,
      selectedIds: string[],
      otherText?: string,
    ) => {
      const labels = ask.options
        .filter((o) => selectedIds.includes(o.id))
        .map((o) => o.label)
      const otherTrimmed = otherText?.trim()
      const parts = [...labels]
      if (otherTrimmed) {
        // Prefer free text over the bare "Other" label when present
        const withoutBareOther = parts.filter(
          (l) => l.trim().toLowerCase() !== 'other',
        )
        withoutBareOther.push(otherTrimmed)
        parts.length = 0
        parts.push(...withoutBareOther)
      } else if (
        selectedIds.includes('__other__') ||
        selectedIds.some((id) => id.toLowerCase() === 'other')
      ) {
        // Should not happen (Continue disabled without text) - keep label
        if (!parts.some((l) => l.trim().toLowerCase() === 'other')) {
          parts.push('Other')
        }
      }
      const joined = parts.join(', ')
      const text = ask.replyTemplate
        ? ask.replyTemplate.replace(/\{answers\}/g, joined)
        : `My choice for "${ask.question}": ${joined}`

      // Collapse the live MCQ to a status badge before the follow-up turn streams.
      const answered = {
        selectedIds,
        summary: joined,
        otherText: otherTrimmed || undefined,
      }

      // FIX 1: Stamp owning conversation id for delayed send (incident 2026-07-22).
      // Capture conversation id at submit time, not at setTimeout fire time, so the send
      // cannot land in a different thread if the user switches conversations mid-delay.
      const owningConversationId =
        streamingConversationIdRef.current ??
        hostedLinkedConversationIdRef.current ??
        currentConversationIdRef.current

      const markAnswered = (
        prev: import('./chat-pane.types').Message[],
      ): import('./chat-pane.types').Message[] =>
        prev.map((m) => {
          if (m.askQuestion?.questionId !== ask.questionId) return m
          const askQuestion = { ...m.askQuestion, answered }
          if (m.messageId) {
            void patchChatMessageMetadata(m.messageId, { askQuestion })
          }
          return { ...m, askQuestion }
        })

      // FIX 3: Persist the AskQuestion assistant segment before submit.
      // Find the assistant message with this AskQuestion and ensure it's persisted
      // before we allow the choice reply to be sent.
      const askQuestionMessage = streamingMessages.find(
        (m) => m.askQuestion?.questionId === ask.questionId
      )
      
      if (askQuestionMessage && !askQuestionMessage.messageId && owningConversationId) {
        // AskQuestion message exists but has no messageId - it's not persisted yet.
        // Persist it before marking answered.
        try {
          const saveResult = await saveMessageToHistory(
            'assistant',
            askQuestionMessage.content,
            owningConversationId,
            {
              metadata: {
                askQuestion: ask,
              },
            },
          )
          
          // Update the message with its new messageId
          const addMessageId = (prev: import('./chat-pane.types').Message[]) =>
            prev.map((m) => {
              if (m.askQuestion?.questionId !== ask.questionId) return m
              return { ...m, messageId: saveResult.messageId ?? undefined }
            })
          setStreamingMessages(addMessageId)
          setLegacyMessages(addMessageId)
        } catch (err) {
          console.error('[ChatPane] Failed to persist AskQuestion before submit:', err)
          // Continue anyway - the backend can handle unpersisted AskQuestions
        }
      }

      setStreamingMessages(markAnswered)
      setLegacyMessages(markAnswered)

      // FIX 1 (continued): Pin the send to the owning conversation.
      // Guard: only send if we're still on the same conversation (detects switches during persist).
      setTimeout(() => {
        const currentConv =
          streamingConversationIdRef.current ??
          hostedLinkedConversationIdRef.current ??
          currentConversationIdRef.current
        
        if (currentConv === owningConversationId) {
          void handleSendRef.current(undefined, text)
        } else {
          console.warn(
            '[ChatPane] AskQuestion submit cancelled: conversation switched during submit',
            { owning: owningConversationId, current: currentConv }
          )
        }
      }, 0)
    },
    [
      setLegacyMessages,
      setStreamingMessages,
      streamingMessages,
      saveMessageToHistory,
    ],
  )

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        message?: string
        targetThreadId?: string
        targetConversationId?: string
        viewContext?: ViewContext
      }>).detail ?? {}
      const { message, targetThreadId, targetConversationId } = detail
      if (typeof message !== 'string' || !message.trim()) return
      if (detail.viewContext && typeof detail.viewContext === 'object' && detail.viewContext.type) {
        viewContextOverrideRef.current = detail.viewContext
      }
      // Require an explicit target so Reply cannot land in an unrelated open chat
      // (e.g. EECONF while viewing a Soul proposal). Unscoped events are ignored.
      if (targetThreadId) {
        if (hostedThreadIdRef.current !== targetThreadId) return
      } else if (targetConversationId) {
        const activeConv =
          streamingConversationId ?? currentConversationId ?? hostedLinkedConversationId ?? null
        if (activeConv !== targetConversationId) return
      } else {
        return
      }
      void handleSendRef.current(undefined, message.trim())
    }
    window.addEventListener('chat-auto-send', handler)
    return () => window.removeEventListener('chat-auto-send', handler)
  }, [streamingConversationId, currentConversationId, hostedLinkedConversationId])

  // All user message indices (p0, p1, ...) for sticky prompt tracking.
  // Skip Plan Build harness rows - they are not prompts to pin.
  const userMsgIndices = useMemo(
    () =>
      streamingMessages.reduce<number[]>((acc, m, i) => {
        if (m.role === 'user' && !isPlanBuildUserText(m.content ?? '')) acc.push(i)
        return acc
      }, []),
    [streamingMessages],
  )

  // Collapse expanded sticky when the pinned prompt changes
  useEffect(() => {
    setStickyPromptExpanded(false)
  }, [stickyUserMsgIndex])

  // Track which user prompt has scrolled above the viewport (Cursor-style: p0, p1, ...)
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const updateStickyUserMsgIndex = () => {
      if (userMsgIndices.length === 0) {
        setStickyUserMsgIndex(null)
        return
      }
      const viewportTop = container.getBoundingClientRect().top
      let pinned: number | null = null
      for (const idx of userMsgIndices) {
        const el = container.querySelector(`[data-message-index="${idx}"]`)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        // Fully above the scroll viewport: show the latest such user prompt in the sticky bar
        if (rect.bottom < viewportTop - 1) {
          if (pinned === null || idx > pinned) pinned = idx
        }
      }
      setStickyUserMsgIndex(pinned)
    }

    updateStickyUserMsgIndex()
    container.addEventListener('scroll', updateStickyUserMsgIndex, { passive: true })
    const resizeObserver = new ResizeObserver(updateStickyUserMsgIndex)
    resizeObserver.observe(container)

    const timerId = setTimeout(updateStickyUserMsgIndex, 50)
    return () => {
      clearTimeout(timerId)
      container.removeEventListener('scroll', updateStickyUserMsgIndex)
      resizeObserver.disconnect()
    }
  }, [userMsgIndices, streamingMessages.length, messagesContainerRef])

  // Use handlers from hooks - these are already extracted

  // Load active conversations
  useEffect(() => {
    const loadActiveConversations = async () => {
      try {
        const response = await getActiveConversations()
        if (response.success) {
          setActiveConversations(response.conversations.map(conv => ({
            id: conv.id,
            title: conv.title,
            message_count: conv.message_count,
            updated_at: conv.updated_at,
            created_at: conv.created_at,
            status: conv.status,
            metadata: conv.metadata,
          })))
        }
      } catch (error) {
        if (!isAbortFetchError(error)) {
          console.error('[ChatPane] Failed to load active conversations:', error)
        }
      }
    }

    loadActiveConversations()
  }, [currentConversationId, historyRefreshKey]) // Refresh when conversation changes or history updates

  // Edit handlers come from hooks (resolveDocumentId built in).

  const firstName = user?.user_metadata?.full_name?.split(' ')[0]
    || user?.user_metadata?.name?.split(' ')[0]
    || user?.email?.split('@')[0]
    || 'there'

  /** Collapse Basecamp ingestion narrative steps into one run card (polling + flipped streaming). */
  const showIngestionProgressGrouping = useMemo(() => {
    const effectiveConversationId = currentConversationId ?? streamingConversationId
    if (!effectiveConversationId && !messagesLookLikeIngestionRun(messages)) return false

    if (effectiveConversationId) {
      const conv = activeConversations.find((c) => c.id === effectiveConversationId)
      const meta = (conv?.metadata ?? {}) as Record<string, unknown>
      const isIngestion =
        meta.type === 'basecamp-ingestion' || meta.type === 'ingestion'
      if (isIngestion) return true
      const title = conv?.title ?? ''
      if (title.trim().toLowerCase().startsWith('ingesting:')) return true
    }

    return messagesLookLikeIngestionRun(messages)
  }, [currentConversationId, streamingConversationId, activeConversations, messages])

  // Process content to replace document IDs with clickable markdown links

  const handleRegenerateLastAssistant = async () => {
    const msgs = streamingMessages
    const lastIdx = msgs.length - 1
    if (lastIdx < 0 || msgs[lastIdx]?.role !== 'assistant') return
    // Scan backwards to find the nearest user message (tool messages may sit between user and final assistant)
    let userIdx = -1
    for (let k = lastIdx - 1; k >= 0; k--) {
      if (msgs[k].role === 'user') { userIdx = k; break }
    }
    if (userIdx < 0) return
    const userMsg = msgs[userIdx]
    const text = userMsg.content?.trim()
    if (!text) {
      toast.error('Cannot regenerate', { description: 'Last user turn is empty.' })
      return
    }
    if (!streamingConversationId) {
      toast.error('Cannot regenerate', { description: 'No conversation id yet.' })
      return
    }
    if (!userMsg.messageId) {
      toast.error('Cannot regenerate', {
        description: 'This user line has no server message id. Reload the thread or send a new message.',
      })
      return
    }
    try {
      const res = await fetchWithAuth('/api/chat-stream/edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: streamingConversationId,
          messageId: userMsg.messageId,
          newContent: text,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!data.success) {
        toast.error('Regenerate failed', { description: data.error || res.statusText || 'Unknown error' })
        return
      }
      const newId = data.newMessageId != null ? String(data.newMessageId) : userMsg.messageId
      const nextMessages = msgs.slice(0, userIdx).concat([
        { ...userMsg, content: text, messageId: newId, editOf: userMsg.messageId },
      ])
      editRepromptBaseRef.current = nextMessages
      setStreamingMessages(nextMessages)
      setTimeout(() => void handleSend(undefined, text), 0)
    } catch (err) {
      console.error('[ChatPane] regenerate failed:', err)
      toast.error('Regenerate failed', { description: err instanceof Error ? err.message : 'Network error' })
    }
  }

  const blankNewChatId = registry?.getBlankNewChatId() ?? null
  const blankNewHostedElsewhere = Boolean(
    blankNewChatId &&
      blankNewChatId !== hostedThreadId &&
      registry?.getBinding(blankNewChatId)?.surface === 'editor',
  )

  const isChatFirstHome = shouldShowChatFirstHome({
    loadingConversation,
    messageCount: messages.length,
    blankNewHostedElsewhere,
    turnCommitted,
  })
  const showComposer = !blankNewHostedElsewhere
  const wideChatSurface = isWideChatSurface(surfaceVariant)
  const messageColumnClass = cn(
    'w-full mx-auto',
    wideChatSurface ? CHAT_EDITOR_FOCUS_MAX_CLASS : CHAT_COMPACT_FOCUS_MAX_CLASS,
  )

  // Document context chip: shown above the composer on the Chat-First Home screen.
  const contextDraft = hostedThreadId ? registry?.getDraft(hostedThreadId) : null
  const contextDocumentId = contextDraft?.contextDocumentId ?? null
  const contextDocumentTitle = contextDraft?.contextDocumentTitle ?? null
  const contextIsSessionScratch =
    isSessionScratchDisplayTitle(contextDocumentTitle) ||
    (Boolean(contextDocumentId) &&
      contextDocumentId === activeDocumentIdRef.current &&
      isSessionScratchPath(activeDocumentPath))
  const visibleContextDocumentId = contextIsSessionScratch ? null : contextDocumentId
  const visibleContextDocumentTitle = contextIsSessionScratch ? null : contextDocumentTitle
  // Stable refs so handleSend can access current values without re-memoising
  const contextDocumentIdRef = useRef(visibleContextDocumentId)
  contextDocumentIdRef.current = visibleContextDocumentId
  const contextDocumentTitleRef = useRef(visibleContextDocumentTitle)
  contextDocumentTitleRef.current = visibleContextDocumentTitle

  /** Workspace pip stays in the widget row; current-doc chip gets its own row below. */
  const composerContextShared = {
    activeDocumentContext,
    dismissedDocIds,
    onDismissActiveDocument: handleDismissActiveDocument,
    onRemoveReference,
    onReferenceOpen: openReferenceFromChat,
    conversationScope,
    onScopeChange: handleScopeChange,
    setConversationScope,
    userExplicitlySetScope,
    workspaces,
    suppressActiveDocId: null,
  }
  const composerScopeSlot = useMemo(
    () => <ChatPaneComposerContextSlot {...composerContextShared} part="scope" />,
    // composerContextShared is rebuilt each render; list the values that change the slot.
    [
      activeDocumentContext,
      conversationScope,
      handleScopeChange,
      handleDismissActiveDocument,
      onRemoveReference,
      openReferenceFromChat,
      dismissedDocIds,
      workspaces,
    ],
  )
  const composerChipSlot = useMemo(
    () => <ChatPaneComposerContextSlot {...composerContextShared} part="chips" />,
    [
      activeDocumentContext,
      conversationScope,
      handleScopeChange,
      handleDismissActiveDocument,
      onRemoveReference,
      openReferenceFromChat,
      dismissedDocIds,
      workspaces,
    ],
  )

  const renderPendingEditsBanner = () => {
    if (modifiedFilesForPanel.length > 0) return null
    if (pendingProposedEdits.length === 0) return null
    return (
      <div
        data-testid={testIds.chatPane.pendingEditsBanner}
        className="mx-3 mb-2 flex items-center justify-between gap-3 rounded-md border border-yellow-500/20 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-600 dark:text-yellow-400"
      >
        <span>
          {pendingProposedEdits.length} proposed change
          {pendingProposedEdits.length === 1 ? '' : 's'} waiting for your review
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => openPendingEditDiff()}
          data-testid={testIds.chatPane.pendingEditsReview}
        >
          Review
        </Button>
      </div>
    )
  }

  const renderComposerInput = (composerClassName?: string) => {
    const inputClassName = cn(
      'transition-colors',
      composerClassName,
      isOver ? 'border-primary/50' : '',
    )

    if (currentConversationId) {
      return (
        <ContentEditableChatInput
          ref={contentEditableInputRef}
          data-testid={testIds.chatPane.ingestionInput}
          value={input}
          references={references}
          onChange={(text) => setLegacyInput(text)}
          onReferencesChange={handleReferencesChange}
          onInsertReference={(ref) => onAddReference?.(ref)}
          onRemoveReference={(id, ref) => onRemoveReference?.(id, ref)}
          onOpenReference={openReferenceFromChat}
          onLocalTextFilesDrop={handleLocalTextFilesDrop}
          onLocalAudioFilesDrop={handleLocalAudioFilesDrop}
          onLongTextPaste={handleLongTextPaste}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && queueEditRef.current) {
              e.preventDefault()
              finishQueueEdit('cancel')
              return
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          placeholder={
            isOver
              ? 'Drop document here to reference...'
              : isPolling
                ? 'Reply to agent...'
                : isChatFirstHome
                  ? 'How can I help you today?'
                  : 'Ask questions or request changes...'
          }
          openTabIds={mentionOpenTabIds}
          recentlyViewedIds={mentionRecentlyViewedIds}
          currentProjectId={null}
          mentionFolderPaths={workspaceScopePaths}
          slashAllowedWorkspaceSlugs={chatScopeWorkspaceSlugs}
          slashWorkspaceMetaBySlug={slashWorkspaceMetaBySlug}
          slashChatScopeLabel={slashChatScopeLabel || undefined}
          slashOrganizationIds={skillScopeOrganizationIds}
          activeChatSkill={activeChatSkill}
          onActiveChatSkillChange={setActiveChatSkill}
          showToolbar={true}
          onSend={handleSend}
          aria-label="Chat input"
          modelBadge={composerModelCluster}
          composerExtras={composerScopeSlot}
          composerContextRow={composerChipSlot}
          className={inputClassName}
          accentStyle={composerAccentStyle}
          skillOffer={skillOffer}
          modeSelector={
            <ChatModeSelector
              value={chatMode}
              onChange={setChatMode}
              onBuild={handleBuildFromPlan}
              buildDisabled={isStreaming || isPolling}
            />
          }
        />
      )
    }

    return (
      <ContentEditableChatInput
        ref={contentEditableInputRef}
        value={input}
        activeChatSkill={activeChatSkill}
        onActiveChatSkillChange={setActiveChatSkill}
        references={references}
        onChange={(text) => setStreamingInput(text)}
        onReferencesChange={handleReferencesChange}
        onInsertReference={(ref) => onAddReference?.(ref)}
        onRemoveReference={(id, ref) => onRemoveReference?.(id, ref)}
        onOpenReference={openReferenceFromChat}
        onLocalTextFilesDrop={handleLocalTextFilesDrop}
        onLocalAudioFilesDrop={handleLocalAudioFilesDrop}
        onLongTextPaste={handleLongTextPaste}
        onImagesChange={(imgs) => {
          devLog('[ChatPane] onImagesChange:', imgs.length, 'images')
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && queueEditRef.current) {
            e.preventDefault()
            finishQueueEdit('cancel')
            return
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (queueEditRef.current || !sendBusy || isStreaming) handleSend()
          }
        }}
        placeholder={
          isOver
            ? 'Drop document here to reference...'
            : isStreaming && !queueEdit
              ? 'Queue next message...'
              : isChatFirstHome
                ? 'How can I help you today?'
                : 'Type @ to mention, or drag files here...'
        }
        openTabIds={mentionOpenTabIds}
        recentlyViewedIds={mentionRecentlyViewedIds}
        currentProjectId={null}
        mentionFolderPaths={workspaceScopePaths}
        slashAllowedWorkspaceSlugs={chatScopeWorkspaceSlugs}
        slashWorkspaceMetaBySlug={slashWorkspaceMetaBySlug}
        slashChatScopeLabel={slashChatScopeLabel || undefined}
        slashOrganizationIds={skillScopeOrganizationIds}
        showToolbar={true}
        onSend={handleSend}
        sendBusy={queueEdit ? false : sendBusy}
        onStop={isStreaming && !queueEdit ? handleStop : undefined}
        aria-label={queueEdit ? 'Editing queued message' : 'Chat input'}
        data-testid={testIds.chatPane.input}
        modelBadge={composerModelCluster}
        composerExtras={composerScopeSlot}
        composerContextRow={composerChipSlot}
        className={inputClassName}
        accentStyle={composerAccentStyle}
        skillOffer={skillOffer}
        modeSelector={
          <ChatModeSelector
            value={chatMode}
            onChange={setChatMode}
            onBuild={handleBuildFromPlan}
            buildDisabled={isStreaming || isPolling}
          />
        }
      />
    )
  }

  return (
    <div
      className={cn(
        'h-full min-w-0 flex flex-col overflow-hidden bg-card panel-hover-glow relative',
        // Compact agent panel sits beside PanelResizeHandle (w-px). border-l
        // would stack a second pixel. Collapsed rail and editor chats have no handle.
        (collapsed || surfaceVariant !== 'compact') && 'border-l border-border',
        CHAT_PANE_CONTAINER_CLASS,
      )}
      data-testid={testIds.chatPane.container}
      onFocusCapture={() => {
        if (hostedThreadId && registry) {
          registry.setFocusedThreadId(hostedThreadId)
        }
      }}
      onBlurCapture={(e) => {
        if (!hostedThreadId || !registry) return
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          registry.setFocusedThreadId(null)
        }
      }}
      onDragEnter={handleNativeDragEnter}
      onDragLeave={handleNativeDragLeave}
      onDragOver={handleNativeDragOver}
      onDragOverCapture={handleChatDragOverCapture}
      onDropCapture={handleChatDropCapture}
    >
      {/* Drop zone overlay */}
      {isNativeDragOver && (
        <ChatPaneDropZoneOverlay />
      )}
      {collapsed ? (
        <ChatPaneCollapsedState onToggleCollapse={onToggleCollapse} />
      ) : (
        <>
          {/* Conversation Header with Tabs (agent panel only; editor uses document-viewer toolbar) */}
          {surfaceVariant !== 'editor' ? (
          <ConversationHeader
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            activeConversations={activeConversations}
            currentConversationId={currentConversationId || streamingConversationId || undefined}
            hostedThreadId={hostedThreadId}
            hostedThreadTitle={_hostedThreadTitle}
            showSurfaceMenu={surfaceVariant === 'compact'}
            onNewChat={handleNewChatWithSkillReset}
            onShowHistory={handleShowHistory}
            onSelectConversation={handleSelectConversationWithReset}
            onCloseConversation={handleCloseConversation}
            onExport={handleExport}
            onCopyTranscript={handleCopyTranscript}
            onCopyConversationId={handleCopyConversationId}
            onRename={handleRename}
            onDelete={() => void handleDeleteConversation()}
            onClearHistory={handleClearHistory}
            onSettings={handleSettings}
            onMoveToEditorTab={handleMoveToEditorTab}
            onFeedback={handleOpenChatFeedback}
            hasChatMessages={hasChatMessages}
            onLearnFromSession={
              hasChatMessages
                ? () => {
                    const nudge =
                      'Review this conversation for durable preferences or project facts worth saving to my `.soul/` (especially `memory.md`). For each, call proposeSoulUpdate with an exact patch (trigger: learn_from_session). If nothing durable, say so - do not invent memories.'
                    void handleSend(undefined, nudge)
                  }
                : undefined
            }
            sessionFilesCount={sessionFileCount}
            sessionFilesOpen={sessionFilesOpen}
            onSessionFilesOpenChange={
              surfaceVariant === 'compact' ? handleSessionFilesOpenChange : undefined
            }
            sessionFilesSlot={
              surfaceVariant === 'compact' && sessionScratchConversationId ? (
                <SessionFilesStrip
                  conversationId={sessionScratchConversationId}
                  onDocumentClick={onDocumentClick}
                  defaultFolderPath={
                    activeDocumentPath && !isSessionScratchPath(activeDocumentPath)
                      ? activeDocumentPath.replace(/\/[^/]+$/, '')
                      : null
                  }
                  workspaces={workspaces.map((w) => ({
                    id: w.id,
                    name: w.name,
                    slug: w.slug,
                    contentRoot: (w.contentRoot ?? '').trim(),
                  }))}
                  visible={sessionFilesOpen}
                  onCountChange={setSessionFileCount}
                />
              ) : null
            }
          />
          ) : null}
          {flowAgentOrigin ? (
            <FlowAgentOriginBar
              origin={flowAgentOrigin}
              onOpenFlow={(flowId) => onDocumentClick?.(flowId)}
            />
          ) : null}

          {/* Messages or Chat-First Home */}
          {blankNewHostedElsewhere ? (
            <div
              className="flex min-h-0 flex-1 flex-col items-center justify-center p-6 text-center"
              data-testid={testIds.chatPane.messages}
            >
              <p className="text-sm text-muted-foreground">
                New chat is open in the editor tab.
              </p>
            </div>
          ) : (
          <div className="relative min-h-0 min-w-0 flex-1">
          <div
            ref={messagesContainerRef}
            className="absolute inset-0 overflow-y-auto overflow-x-hidden chat-messages-scroll"
            data-testid={testIds.chatPane.messages}
            aria-label="Chat messages"
          >
            {isChatFirstHome ? (
              <ChatFirstHome
                userName={firstName}
                variant={wideChatSurface ? 'comfortable' : 'compact'}
                data-testid={testIds.chatPane.welcome}
                className="h-full min-h-full chat-turn-fade-in"
              />
            ) : (
            <>
            {/* Sticky prompt: flush to scroll top (no parent padding); tracks any user turn scrolled above viewport */}
            {(() => {
              if (stickyUserMsgIndex === null) return null
              const stickyUserMsg = streamingMessages[stickyUserMsgIndex]
              if (!stickyUserMsg) return null
              const hasContent = stickyUserMsg.content && stickyUserMsg.content !== '(image)'
              const hasRefs =
                stickyUserMsg.references &&
                stickyUserMsg.references.length > 0 &&
                !hasFlowNoteInlineMarkers(stickyUserMsg.content)
              const hasImages = stickyUserMsg.images && stickyUserMsg.images.length > 0
              if (!hasContent && !hasRefs && !hasImages) return null
              const isUpdateSeed = isUpdateWorkSpecSeedPrompt(stickyUserMsg.content)
              const stickyCollapsed = !isUpdateSeed && !stickyPromptExpanded
              return (
                <div className="sticky top-0 z-10 mb-2 border-b border-border bg-background/95 backdrop-blur-sm">
                  <div className={cn(messageColumnClass, chatMessageGutterClassName('pt-2 pb-0', surfaceVariant))}>
                    <div
                      role={isUpdateSeed ? undefined : 'button'}
                      tabIndex={isUpdateSeed ? undefined : 0}
                      onClick={isUpdateSeed ? undefined : () => setStickyPromptExpanded((v) => !v)}
                      onKeyDown={
                        isUpdateSeed
                          ? undefined
                          : (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setStickyPromptExpanded((v) => !v)
                              }
                            }
                      }
                      className={cn('w-full text-left group', !isUpdateSeed && 'cursor-pointer')}
                      aria-label={
                        isUpdateSeed
                          ? 'Update work spec'
                          : stickyPromptExpanded
                            ? 'Collapse prompt'
                            : 'Expand prompt'
                      }
                      aria-expanded={isUpdateSeed ? undefined : stickyPromptExpanded}
                    >
                      <div
                        className={cn(
                          'relative inline-block w-fit max-w-[min(100%,calc(100%-1.75rem))] min-w-0 rounded-2xl border px-4 py-3 text-left overflow-hidden transition-[colors,box-shadow,background-color,background] duration-300',
                          stickyCollapsed && 'max-h-[4.5rem]',
                          chatScopeAccent
                            ? ''
                            : 'border-primary/20 bg-primary/10',
                        )}
                        style={chatScopeAccent?.bubble}
                      >
                        {hasImages && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <ChatPostedImages images={stickyUserMsg.images!} />
                          </div>
                        )}
                        {hasContent && (
                          <UserPromptMarkdownBody
                            content={stickyUserMsg.content}
                            onOpenReference={openReferenceFromChat}
                          />
                        )}
                        {hasRefs && <MessageReferences references={stickyUserMsg.references!} />}
                        {stickyCollapsed && (
                          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-primary/10 to-transparent pointer-events-none" />
                        )}
                      </div>
                      {!isUpdateSeed && (
                        <div className="flex justify-center pb-1.5 pt-0.5">
                          <ChevronDown
                            className={cn(
                              'h-3.5 w-3.5 text-muted-foreground/50 transition-transform group-hover:text-muted-foreground',
                              stickyPromptExpanded && 'rotate-180',
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })()}
            <div className={cn(chatMessageGutterClassName(undefined, surfaceVariant), 'pb-4 pt-4')}>
        {loadingConversation && !turnCommitted ? (
          // Loading state when opening a conversation (e.g. ingestion)
          <div className="flex-1 flex flex-col items-center justify-center text-center pb-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">Loading conversation...</p>
          </div>
        ) : (
          // Chat Messages - Cursor Style (no bubbles for AI, editable textarea for user)
          <div className="space-y-1.5">
            {messages.length === 0 && turnCommitted && committedUserTurnRef.current ? (
              <div data-message-index={0} className="chat-turn-fade-in">
                <div className="mb-3 w-full max-w-full min-w-0">
                  <div
                    className="relative inline-block w-fit max-w-[min(100%,calc(100%-1.75rem))] min-w-0 rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-left"
                    style={chatScopeAccent?.bubble}
                  >
                    {committedUserTurnRef.current.images && committedUserTurnRef.current.images.length > 0 ? (
                      <ChatPostedImages images={committedUserTurnRef.current.images} />
                    ) : null}
                    {committedUserTurnRef.current.content && committedUserTurnRef.current.content !== '(image)' ? (
                      <UserPromptMarkdownBody
                        content={committedUserTurnRef.current.content}
                        onOpenReference={openReferenceFromChat}
                      />
                    ) : null}
                    {committedUserTurnRef.current.references && committedUserTurnRef.current.references.length > 0 ? (
                      <MessageReferences references={committedUserTurnRef.current.references} />
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            {/* Group ALL consecutive tool messages (across rounds) into a single collapsible block */}
            {(() => {
              type ThinkingGroup = {
                type: 'thinking'
                messages: Message[]
                firstIndex: number
                isLastTurnGroup: boolean
              }
              type IngestionGroup = {
                type: 'ingestion'
                messages: Message[]
                firstIndex: number
                runSummary?: { label: string; status: 'running' | 'success' | 'error' }
              }
              type SingleItem = { type: 'single'; message: Message; index: number }
              type DisplayItem = ThinkingGroup | IngestionGroup | SingleItem

              /** After a user message, emit tools/batches before assistant prose (fixes sticky tools under summary). */
              const flushAssistantTurn = (turnItems: DisplayItem[], isLastBufferedTurn: boolean) => {
                if (turnItems.length === 0) return
                const tools: DisplayItem[] = []
                const rest: DisplayItem[] = []
                for (const item of turnItems) {
                  if (item.type === 'thinking') {
                    tools.push(item)
                    continue
                  }
                  if (item.type === 'single' && item.message.role === 'tool') {
                    tools.push(item)
                    continue
                  }
                  rest.push(item)
                }
                if (tools.length > 0) {
                  const thinkingMessages: Message[] = []
                  const firstClassTools: DisplayItem[] = []
                  let firstIndex = messages.length
                  for (const toolItem of tools) {
                    const itemFirstIndex =
                      toolItem.type === 'single' ? toolItem.index : toolItem.firstIndex
                    firstIndex = Math.min(firstIndex, itemFirstIndex)
                    if (toolItem.type === 'single' && toolItem.message.role === 'tool') {
                      if (isFirstClassToolWidget(toolItem.message)) {
                        firstClassTools.push(toolItem)
                      } else {
                        thinkingMessages.push(toolItem.message)
                      }
                      continue
                    }
                    if (toolItem.type === 'thinking') {
                      thinkingMessages.push(...toolItem.messages)
                    }
                  }
                  if (thinkingMessages.length > 0) {
                    displayItems.push({
                      type: 'thinking',
                      messages: thinkingMessages,
                      firstIndex,
                      isLastTurnGroup: isLastBufferedTurn,
                    })
                  }
                  displayItems.push(...firstClassTools)
                }
                displayItems.push(...rest)
              }

              const displayItems: DisplayItem[] = []
              const turnBuffer: DisplayItem[] = []
              let i = 0
              while (i < messages.length) {
                const msg = messages[i]
                if (msg.role === 'user') {
                  flushAssistantTurn(turnBuffer, false)
                  turnBuffer.length = 0
                  displayItems.push({ type: 'single', message: msg, index: i })
                  i++
                  continue
                }
                if (msg.role === 'tool') {
                  if (isFirstClassToolWidget(msg)) {
                    turnBuffer.push({ type: 'single', message: msg, index: i })
                    i++
                    continue
                  }
                  const thinkingBatch: Message[] = [msg]
                  let j = i + 1
                  while (
                    j < messages.length &&
                    messages[j].role === 'tool' &&
                    !isFirstClassToolWidget(messages[j])
                  ) {
                    thinkingBatch.push(messages[j])
                    j++
                  }
                  turnBuffer.push({
                    type: 'thinking',
                    messages: thinkingBatch,
                    firstIndex: i,
                    isLastTurnGroup: false,
                  })
                  i = j
                  continue
                }
                // Basecamp ingestion threads: collapse *progress* assistant rows only; keep the
                // terminal success message (prose + FileSummary) as a normal AssistantMessage.
                // Only pull ingestion-shaped rows into the card - follow-up agent chat in the
                // same "Ingesting:" conversation must not spin forever as "Got both IDs."
                if (
                  showIngestionProgressGrouping &&
                  shouldGroupAssistantMessagesAsIngestionRun(messages, i)
                ) {
                  const group = collectIngestionAssistantGroup(messages, i)
                  const j = i + group.length
                  const ti = findIngestionTerminalIndex(group)
                  /** Agentic tool/round cap attaches to an assistant row - must not hide inside IngestionRunCard */
                  const capIdx = group.findIndex((m) => m.cappedAt != null)
                  const splitAt = capIdx >= 0 ? capIdx : ti
                  if (group.length === 1 && splitAt < 0) {
                    turnBuffer.push({ type: 'single', message: msg, index: i })
                    i = j
                    continue
                  }
                  if (splitAt < 0) {
                    // No terminal row yet. If later messages exist (agent continued), stop the
                    // spinner so the card does not look stuck after ingestion finished silently.
                    const hasFollowUp = j < messages.length
                    turnBuffer.push({
                      type: 'ingestion',
                      messages: group,
                      firstIndex: i,
                      ...(hasFollowUp
                        ? { runSummary: { label: 'Ingestion complete', status: 'success' as const } }
                        : {}),
                    })
                    i = j
                    continue
                  }
                  const progress = group.slice(0, splitAt)
                  if (progress.length > 0) {
                    turnBuffer.push({
                      type: 'ingestion',
                      messages: progress,
                      firstIndex: i,
                      runSummary: buildRunHeaderLabel([group[splitAt]]),
                    })
                  }
                  for (let k = splitAt; k < group.length; k++) {
                    turnBuffer.push({ type: 'single', message: group[k], index: i + k })
                  }
                  i = j
                  continue
                }
                turnBuffer.push({ type: 'single', message: msg, index: i })
                i++
              }
              flushAssistantTurn(turnBuffer, true)

              const turnCopyByIndex = buildAssistantTurnCopyByIndex(messages)
              const pendingThinkingLabel =
                [...thinkingOperations].reverse().find((op) => op.status === 'active')?.label ??
                null

              return displayItems.map((item, _displayIdx) => {
              if (item.type === 'thinking') {
                const isAfterEditedMessage =
                  editingMessageIndex !== null && item.firstIndex > editingMessageIndex
                return (
                  <div
                    key={`thinking-${item.firstIndex}`}
                    className={cn(messageColumnClass, 'transition-opacity', isAfterEditedMessage && 'opacity-40')}
                  >
                    <ThinkingTimeline
                      messages={item.messages}
                      isStreaming={isStreaming}
                      isLastTurnGroup={item.isLastTurnGroup}
                      liveLabel={item.isLastTurnGroup ? pendingThinkingLabel : null}
                      onDocumentClick={onDocumentClick}
                      activeDocumentId={activeDocumentIdRef.current}
                    />
                  </div>
                )
              }

              if (item.type === 'ingestion') {
                const isAfterEditedMessage = editingMessageIndex !== null && item.firstIndex > editingMessageIndex
                return (
                  <div
                    key={`ingestion-${item.firstIndex}`}
                    className={cn(messageColumnClass, "transition-opacity", isAfterEditedMessage && "opacity-40")}
                  >
                    <IngestionRunCard
                      messages={item.messages}
                      runSummary={item.runSummary}
                      onDocumentClick={onDocumentClick}
                    />
                  </div>
                )
              }

              const { message, index: i } = item
              // Grey out messages after the one being edited (Cursor-style)
              const isAfterEditedMessage = editingMessageIndex !== null && i > editingMessageIndex

              return (
              <div
                key={i}
                data-message-index={i}
                className={cn(
                  messageColumnClass,
                  "transition-opacity",
                  isAfterEditedMessage && "opacity-40"
                )}
              >
                {message.role === "tool" ? (
                  <div data-testid={testIds.chatPane.messageToolCall(i)}>
                    <ToolMessage
                      message={message}
                      onDocumentClick={onDocumentClick}
                      activeDocumentId={activeDocumentIdRef.current}
                    />
                  </div>
                ) : message.role === "assistant" ? (
                  <>
                  <AssistantMessage
                    message={messageForAssistantDisplay(messages, i)}
                    hideTrailingTimestamp={
                      !shouldShowAssistantTimestamp(messages, i, { isStreaming })
                    }
                    isAfterEdited={isAfterEditedMessage}
                    copiedCode={copiedCode}
                    onCopyCode={handleCopyCode}
                    onDocumentClick={onDocumentClick}
                    proposedEdit={message.proposedEdit}
                    onAcceptEdit={acceptFromChrome}
                    onRejectEdit={rejectFromChrome}
                    hidePendingFileWidget={
                      message.proposedEdit?.status === 'pending' &&
                      modifiedFilesForPanel.some(
                        (row) =>
                          row.pendingEdit != null &&
                          proposedEditMatches(row.pendingEdit, message.proposedEdit!),
                      )
                    }
                    hideTurnUndo={modifiedFilesForPanel.length > 0}
                    onUndoAcceptedEdit={async (edit) => {
                      try {
                        const res = await fetchWithAuth('/api/chat-stream/undo-edit', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ documentId: edit.documentId }),
                        })
                        const data = await res.json()
                        if (data.success) {
                          toast.success('Changes undone', { description: edit.title })
                          setStreamingMessages(prev => prev.map((m, idx) =>
                            idx === i && m.proposedEdit
                              ? { ...m, proposedEdit: { ...m.proposedEdit, status: 'rejected' as const, changeStatus: 'rejected' } }
                              : m
                          ))
                          window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: edit.documentId } }))
                        } else {
                          toast.error('Undo failed', { description: data.error })
                        }
                      } catch {
                        toast.error('Undo failed', { description: 'Network error' })
                      }
                    }}
                    isApplyingEdit={isApplyingEdit}
                    activeDocumentId={activeDocumentIdRef.current}
                    activeDocumentTitle={activeDocumentTitleRef.current}
                    activeDocumentIdRef={activeDocumentIdRef}
                    activeDocumentTitleRef={activeDocumentTitleRef}
                    isLastMessage={i === messages.length - 1}
                    onRetryWithExplicitTool={(prompt) =>
                      currentConversationId ? setLegacyInput(prompt) : setStreamingInput(prompt)
                    }
                    onSetProposedEdit={setProposedEdit}
                    onSetDiffPreviewOpen={(open) => {
                      if (open && message.proposedEdit) {
                        handleReviewProposedEdit(message.proposedEdit)
                      } else {
                        setDiffPreviewOpen(false)
                      }
                    }}
                    onActionChoice={(choice) => handleActionChoiceClick(choice, message, i)}
                    onAskQuestionSubmit={handleAskQuestionSubmit}
                    onFlashCardPersist={(patch) => {
                      setStreamingMessages((prev) =>
                        prev.map((m, idx) => {
                          if (idx !== i || !m.flashCardSession) return m
                          const flashCardSession = { ...m.flashCardSession, ...patch }
                          if (m.messageId) {
                            void patchChatMessageMetadata(m.messageId, { flashCardSession })
                          }
                          return { ...m, flashCardSession }
                        }),
                      )
                    }}
                    chatMode={chatMode}
                    onUndoCheckpoints={async (cpIds) => {
                      try {
                        const res = await fetchWithAuth('/api/chat-stream/checkpoint/restore', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ checkpointIds: cpIds }),
                        })
                        const data = await res.json()
                        if (data.success) {
                          toast.success(`Restored ${data.restored.length} document${data.restored.length !== 1 ? 's' : ''}`)
                          // Remove checkpoint button from this message
                          setStreamingMessages(prev => prev.map((m, idx) =>
                            idx === i ? { ...m, checkpointIds: undefined } : m
                          ))
                          // Dispatch document-updated for each restored doc
                          for (const r of data.restored) {
                            if (r.documentId) {
                              window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: r.documentId } }))
                            }
                          }
                        } else {
                          toast.error('Restore failed', { description: data.errors?.[0]?.error })
                        }
                      } catch (err) {
                        toast.error('Restore failed', { description: 'Network error' })
                      }
                    }}
                    onRepromptAfterStop={
                      !currentConversationId && message.aborted
                        ? () => {
                            const base = streamingMessages.slice(0, i)
                            const lastUser = [...base].reverse().find((m) => m.role === 'user')
                            const text = lastUser?.content?.trim()
                            if (!text) return
                            editRepromptBaseRef.current = base
                            setStreamingMessages(base)
                            setTimeout(() => void handleSend(undefined, text), 0)
                          }
                        : undefined
                    }
                    isStreaming={isStreaming && i === messages.length - 1}
                    isChatStreaming={isStreaming}
                    hasLaterAssistant={hasLaterAssistant(messages, i)}
                    isTyping={isTyping}
                    onRegenerate={
                      !currentConversationId && i === messages.length - 1
                        ? handleRegenerateLastAssistant
                        : undefined
                    }
                    onContinue={
                      message.cappedAt ? () => void handleSend(undefined, 'continue') : undefined
                    }
                    showTurnCopy={turnCopyByIndex.has(i)}
                    turnCopyText={turnCopyByIndex.get(i) ?? null}
                  />
                  {message.agentDispatched && (
                    <div className="mt-2 rounded-lg border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm">
                      <div className="flex items-center gap-2 font-medium text-blue-400 mb-1">
                        <span>⚡ Cloud agent dispatched</span>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        <span className="font-medium text-foreground">{message.agentDispatched.title}</span>
                        {' '}→ <code className="text-xs bg-muted px-1 rounded">{message.agentDispatched.targetRepo}</code>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Agent <code className="text-xs bg-muted px-1 rounded">{message.agentDispatched.agentId}</code> is implementing the handoff and will open a merge request when done.
                      </p>
                    </div>
                  )}
                  </>
                ) : (
                  <UserMessage
                    message={message}
                    isEditing={editingMessageIndex === i}
                    editContent={editContent}
                    onEditContentChange={setEditContent}
                    onStartEdit={() => {
                      setEditContent(message.content)
                      setEditingMessageIndex(i)
                    }}
                    onCancelEdit={() => {
                      setEditingMessageIndex(null)
                      setEditContent("")
                    }}
                    editModelBadge={composerModelCluster}
                    references={references}
                    onInsertReference={onAddReference}
                    onRemoveReference={onRemoveReference}
                    onOpenReference={openReferenceFromChat}
                    openTabIds={mentionOpenTabIds}
                    recentlyViewedIds={mentionRecentlyViewedIds}
                    currentProjectId={null}
                    mentionFolderPaths={workspaceScopePaths}
                    slashAllowedWorkspaceSlugs={chatScopeWorkspaceSlugs}
                    slashWorkspaceMetaBySlug={slashWorkspaceMetaBySlug}
                    slashChatScopeLabel={slashChatScopeLabel || undefined}
                    slashOrganizationIds={skillScopeOrganizationIds}
                    activeChatSkill={activeChatSkill}
                    onActiveChatSkillChange={setActiveChatSkill}
                    accentStyle={chatScopeAccent?.bubble ?? null}
                    data-testid={testIds.chatPane.editInput}
                    onSaveEdit={async (newContent: string, images) => {
                      const trimmed = newContent.trim()
                      const editImages = images ?? message.images ?? []
                      const persistContent = trimmed || (editImages.length ? '(image)' : '')
                      if (!persistContent) return

                      let newMessageId: string | undefined
                      if (streamingConversationId && message.messageId) {
                        try {
                          const res = await fetchWithAuth('/api/chat-stream/edit', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              conversationId: streamingConversationId,
                              messageId: message.messageId,
                              newContent: persistContent,
                            }),
                          })
                          const data = await res.json().catch(() => ({}))
                          if (data.success && data.newMessageId != null) {
                            newMessageId = String(data.newMessageId)
                          } else if (!data.success) {
                            toast.warning('Server did not confirm rewind', {
                              description: data.error || res.statusText || 'Continuing from edited prompt locally.',
                            })
                          }
                        } catch (err) {
                          console.warn('[ChatPane] Edit endpoint failed:', err)
                          toast.warning('Rewind may be local only', {
                            description: err instanceof Error ? err.message : 'Network error',
                          })
                        }
                      } else if (streamingConversationId && !message.messageId) {
                        toast.warning('Rewind saved in the UI only', {
                          description:
                            'This user line has no server message id yet, so the server history was not trimmed.',
                        })
                      }

                      const nextMessages = streamingMessages.slice(0, i).concat([
                        {
                          ...message,
                          content: persistContent,
                          images: editImages.length ? editImages : undefined,
                          editOf: message.messageId,
                          messageId: newMessageId ?? message.messageId,
                        },
                      ])
                      editRepromptBaseRef.current = nextMessages
                      setStreamingMessages(nextMessages)
                      setEditingMessageIndex(null)
                      setEditContent('')

                      toast.success('Conversation rewound', {
                        description: 'Generating a new reply from your edited prompt.',
                        duration: 5000,
                      })

                      setTimeout(() => void handleSend(undefined, persistContent, { images: editImages }), 0)
                    }}
                  />
                )}
              </div>
            )
            })
            })()}

            {/* One wait indicator. Hide these dots only while the persona panel is mounted. */}
            {isTyping &&
              !(
                thinkingModeActive &&
                thinkingOperations.some((op) => op.type === 'persona') &&
                !streamingMessages.some((m) => m.role === 'tool')
              ) && (
              <div className={cn(messageColumnClass, 'py-3 chat-turn-fade-in')} role="status" aria-label="Thinking">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Thinking</span>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '200ms' }} />
                    <div className="w-2 h-2 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '400ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Thinking Mode Panel - ingestion/persona path; agentic inline tools use message rows instead */}
            {thinkingModeActive &&
              thinkingOperations.some((op) => op.type === 'persona') &&
              !streamingMessages.some((m) => m.role === 'tool') && (
              <ThinkingModePanel
                thinkingOperations={thinkingOperations.filter((op) => op.type === 'persona')}
                currentTask={null}
                completedFiles={[]}
                agenticRound={agenticRound}
                onViewFile={async (filePath) => {
                  // First try to find documentId from completedFiles
                  const file = completedFiles.find(f => f.filePath === filePath)
                  if (file?.documentId) {
                    devLog('[ChatPane] Opening document from completedFiles:', file.documentId)
                    onDocumentClick?.(file.documentId)
                    return
                  }

                  // If not found, resolve from file path via API
                  try {
                    const response = await fetchWithAuth(`/api/documents/by-path?path=${encodeURIComponent(filePath)}`)
                    if (response.ok) {
                      const doc = await response.json()
                      devLog('[ChatPane] Resolved document ID from path:', doc.id)
                      onDocumentClick?.(doc.id)
                    } else {
                      console.warn('[ChatPane] Document not found by path:', filePath)
                      toast.error('File not found', {
                        description: `Could not find document: ${filePath}`
                      })
                    }
                  } catch (error) {
                    console.error('[ChatPane] Failed to resolve document path:', error)
                    toast.error('Error opening file', {
                      description: error instanceof Error ? error.message : 'Unknown error'
                    })
                  }
                }}
                onUndoAll={() => {
                  toast.info('Undo all changes - feature coming soon')
                }}
                onKeepAll={() => {
                  toast.info('Keep all changes - feature coming soon')
                }}
              />
            )}

          </div>
        )}

            {/* Basecamp notes draft - same focus column as messages (chat-focus-layout) */}
            {pendingBasecampDraft && (
              <div className={cn(messageColumnClass, 'pb-4 pt-2 w-full')}>
                <BasecampNotesDraftPanel
                  draft={pendingBasecampDraft}
                  onDismiss={() => setPendingBasecampDraft(null)}
                  onPushed={() => {
                    setPendingBasecampDraft((prev) => {
                      if (!prev) return prev
                      const next = { ...prev, status: 'pushed' as const }
                      persistBasecampNotesOutcome(hostedLinkedConversationId, next)
                      persistBasecampNotesOutcome(streamingConversationId, next)
                      persistBasecampNotesOutcome(hostedThreadId, next)
                      return next
                    })
                    dispatchBasecampTodoRefreshFromNotesWrite({
                      todoId: pendingBasecampDraft.todoId,
                      todoUrl: pendingBasecampDraft.todoUrl,
                    })
                  }}
                />
              </div>
            )}
            {pendingBasecampCreateDraft && (
              <div className={cn(messageColumnClass, 'pb-4 pt-2 w-full')}>
                <BasecampCreateTodoDraftPanel
                  draft={pendingBasecampCreateDraft}
                  onDismiss={() => {
                    markBasecampCreateDraftConsumed(streamingConversationId)
                    markBasecampCreateDraftConsumed(hostedLinkedConversationId)
                    markBasecampCreateDraftConsumed(hostedThreadId)
                    setPendingBasecampCreateDraft(null)
                  }}
                  onCreated={() => {
                    markBasecampCreateDraftConsumed(streamingConversationId)
                    markBasecampCreateDraftConsumed(hostedLinkedConversationId)
                    markBasecampCreateDraftConsumed(hostedThreadId)
                  }}
                />
              </div>
            )}
            </div>
            </>
            )}
      </div>
          {/* Jump when content sits below the viewport. userHasScrolled alone must not
              keep the pill up after the user (or stream) settles at the true bottom. */}
          {!isNearBottom && streamingMessages.length > 0 ? (
            <button
              type="button"
              data-testid={testIds.chatPane.jumpToBottom}
              onClick={() => {
                const el = messagesContainerRef.current
                if (!el) return
                el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
                userHasScrolledRef.current = false
                setUserHasScrolled(false)
                setIsNearBottom(true)
              }}
              className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-border bg-background/95 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-md backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Jump to bottom of chat"
            >
              <ArrowDown className="h-3.5 w-3.5" />
              Jump to bottom
            </button>
          ) : null}
      </div>
          )}

      {/* Modified files bar above prompt (direct writes + pending proposed edits) */}
      {modifiedFilesForPanel.length > 0 && (
        <div className={chatMessageGutterClassName(undefined, surfaceVariant)}>
          <div className={cn(wideChatSurface ? chatEditorFocusColumnClassName() : cn('mx-auto w-full', CHAT_COMPACT_FOCUS_MAX_CLASS))}>
            <CollapsibleModifiedFilesPanel
              expanded={modifiedFilesPanelExpanded}
              onToggle={() => setModifiedFilesPanelExpanded((e) => !e)}
              files={modifiedFilesForPanel}
              showReview={modifiedFilesForPanel.some((f) => f.pendingEdit)}
              showDecisions={!documentReview}
              onFileClick={(filePath, file) => {
                const row = (file ?? modifiedFilesForPanel.find((f) => f.filePath === filePath)) as
                  | ChatModifiedFileRow
                  | undefined
                if (row?.pendingEdit) {
                  handleReviewProposedEdit(row.pendingEdit)
                  return
                }
                if (file) {
                  void openCompletedFile(file)
                  return
                }
                void openCompletedFile({ filePath })
              }}
              onUndoAll={async () => {
                const pendingRows = modifiedFilesForPanel.filter((f) => f.pendingEdit)
                if (pendingRows.length > 0) {
                  for (const row of pendingRows) {
                    if (row.pendingEdit) await rejectFromChrome(row.pendingEdit)
                  }
                }
                const fileIds = completedFiles
                  .map(f => f.documentId)
                  .filter((id): id is string => Boolean(id))
                if (fileIds.length === 0 && pendingRows.length > 0) return
                if (fileIds.length === 0) {
                  toast.info('No document IDs available to undo')
                  return
                }
                try {
                  const results = await Promise.allSettled(
                    fileIds.map(docId =>
                      fetchWithAuth('/api/chat-stream/undo-edit', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ documentId: docId }),
                      }).then(r => r.json())
                    )
                  )
                  const succeeded = results.filter(r => r.status === 'fulfilled' && (r.value as any).success).length
                  if (succeeded > 0) {
                    toast.success(`Undid ${succeeded} file${succeeded !== 1 ? 's' : ''}`)
                    for (const f of completedFiles) {
                      if (f.documentId) {
                        window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: f.documentId } }))
                      }
                    }
                    setCompletedFiles([])
                  } else if (pendingRows.length === 0) {
                    toast.error('Undo failed', { description: 'Could not undo changes' })
                  }
                } catch {
                  toast.error('Undo failed', { description: 'Network error' })
                }
              }}
              onKeepAll={async () => {
                const pendingRows = modifiedFilesForPanel.filter((f) => f.pendingEdit)
                for (const row of pendingRows) {
                  if (row.pendingEdit) await acceptFromChrome(row.pendingEdit)
                }
                // Keep = accept any pending diffs, then dismiss the bar (files stay on disk;
                // accept toast still offers Undo). Avoids "still pending" after Review.
                setDismissedModifiedFileKeys((prev) => {
                  const next = new Set(prev)
                  for (const row of modifiedFilesForPanel) {
                    next.add(row.documentId || row.filePath)
                  }
                  return next
                })
                setCompletedFiles([])
              }}
              onReview={handleReviewModifiedFiles}
              onUndoFile={async (filePath) => {
                const row = modifiedFilesForPanel.find((f) => f.filePath === filePath)
                if (row?.pendingEdit) {
                  await rejectFromChrome(row.pendingEdit)
                  return
                }
                const file = completedFiles.find((f) => f.filePath === filePath)
                const docId = file?.documentId
                if (!docId) {
                  toast.error('Cannot undo', { description: 'Missing document id for this file.' })
                  return
                }
                try {
                  const undoResponse = await fetchWithAuth('/api/chat-stream/undo-edit', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ documentId: docId }),
                  })
                  const undoResult = await undoResponse.json()
                  if (undoResult.success) {
                    toast.success('Changes undone', { description: filePath.split('/').pop() })
                    window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: docId } }))
                    setCompletedFiles((prev) => prev.filter((f) => f.filePath !== filePath))
                  } else {
                    toast.error('Undo failed', { description: undoResult.error })
                  }
                } catch (error) {
                  toast.error('Undo failed', {
                    description: error instanceof Error ? error.message : 'Unknown error',
                  })
                }
              }}
              onKeepFile={async (filePath) => {
                const row = modifiedFilesForPanel.find((f) => f.filePath === filePath)
                if (row?.pendingEdit) {
                  await acceptFromChrome(row.pendingEdit)
                }
                setDismissedModifiedFileKeys((prev) => {
                  const next = new Set(prev)
                  next.add(row?.documentId || filePath)
                  return next
                })
                setCompletedFiles((prev) => prev.filter((f) => f.filePath !== filePath))
              }}
            />          </div>
        </div>
      )}

      {/* Input */}
      {showComposer && (
      <div
        className={cn(
          'border-t border-border',
          chatComposerGutterClassName(chatComposerFooterPadClassName(surfaceVariant), surfaceVariant),
        )}
        data-testid={testIds.chatPane.composerFooter}
      >
        <div
          className={cn(
            'mx-auto w-full',
            wideChatSurface ? CHAT_EDITOR_FOCUS_MAX_CLASS : CHAT_COMPACT_FOCUS_MAX_CLASS,
          )}
        >
        {/* Queued messages - Cursor-style full-width list */}
        {queuedMessages.length > 0 && (
          <div
            className="border-t border-border/50 bg-muted/20"
            data-testid={testIds.chatPane.queueStrip}
          >
            {/* Header row */}
            <button
              type="button"
              onClick={() => setQueuePanelExpanded(v => !v)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {queuePanelExpanded
                ? <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
              <span className="font-medium">{queuedMessages.length} Queued</span>
              {isStreaming && (
                <span className="ml-auto text-muted-foreground/60">
                  {queueEdit?.id && queueEdit.id === queuedMessages[0]?.id
                    ? 'Oldest row held while editing'
                    : 'Sends after this reply finishes'}
                </span>
              )}
            </button>

            {/* Expanded list */}
            {queuePanelExpanded && (
              <div className="divide-y divide-border/40">
                {queuedMessages.map((item, i) => {
                  const editingRow = Boolean(item.id && queueEdit?.id === item.id)
                  return (
                  <div
                    key={item.id ?? i}
                    className={cn(
                      'group flex items-center gap-2 px-3 py-1.5',
                      editingRow && 'mx-2 my-1 rounded-md bg-primary/15 ring-1 ring-primary/50',
                    )}
                    data-testid={testIds.chatPane.queueItem}
                  >
                    {item.images.length > 0 && (
                      <div
                        className="flex items-center gap-1 shrink-0"
                        data-testid={testIds.chatPane.queueItemImages}
                      >
                        {item.images.slice(0, 2).map((img) => (
                          <img
                            key={img.id}
                            src={img.dataUrl}
                            alt=""
                            className="h-8 w-8 object-cover rounded border border-border"
                          />
                        ))}
                        {item.images.length > 2 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{item.images.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => beginQueueEdit(item)}
                      className="flex-1 min-w-0 text-left"
                      aria-label="Edit queued message"
                    >
                      <p className="text-xs truncate text-foreground/80">{queuedMessagePreview(item)}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                        {isStreaming ? 'Sends after message finishes' : 'Queued'}
                      </p>
                    </button>
                    {item.id && (
                      <button
                        type="button"
                        onClick={() => beginQueueEdit(item)}
                        className={cn(
                          'inline-flex items-center justify-center rounded shrink-0 overflow-hidden',
                          'w-0 p-0 opacity-0 pointer-events-none',
                          'group-hover:w-7 group-hover:opacity-100 group-hover:pointer-events-auto',
                          'focus-visible:w-7 focus-visible:p-1 focus-visible:opacity-100 focus-visible:pointer-events-auto',
                          'transition-[width,opacity] duration-150',
                          editingRow
                            ? 'text-primary group-hover:bg-primary/20'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                        )}
                        aria-label="Edit queued message"
                        data-testid={testIds.chatPane.queueItemEdit(item.id)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSendNow(item, i)}
                      className="text-xs px-2 py-0.5 rounded text-primary hover:bg-primary/10 transition-colors shrink-0"
                      aria-label="Send this message now"
                      data-testid={testIds.chatPane.queueItemSendNow}
                    >
                      Send now
                    </button>
                    <button
                      type="button"
                      onClick={() => removeQueuedRow(item, i)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-muted rounded shrink-0"
                      aria-label="Cancel queued message"
                    >
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {queueEdit && (
          <div
            className="flex items-center justify-between gap-3 px-3 py-2 bg-primary/15 border-y border-primary/30"
            data-testid={testIds.chatPane.queueEditBanner}
          >
            <span className="text-sm font-medium">Editing queued message</span>
            <button
              type="button"
              onClick={() => finishQueueEdit('cancel')}
              className="text-sm font-medium text-primary hover:underline"
              data-testid={testIds.chatPane.queueEditCancel}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Input Area */}
        <form
          onSubmit={handleSend}
          ref={setNodeRef}
          className={`transition-colors ${
            isOver ? "bg-primary/10 border-primary/50" : ""
          }`}
        >
          {renderPendingEditsBanner()}
          {renderComposerInput()}
        </form>

        {/* Drop Zone Hint */}
        {isOver && (
          <div className="mt-2 text-xs text-primary text-center">
            Drop document to add as reference
          </div>
        )}
        </div>
      </div>
      )}

          {/* Conversation rename (agent ... menu + editor toolbar) */}
          {renameTarget && (
            <ConversationRenameDialog
              open={renameDialogOpen}
              onOpenChange={(open) => {
                setRenameDialogOpen(open)
                if (!open) setRenameTarget(null)
              }}
              conversationId={renameTarget.id}
              currentTitle={renameTarget.title}
              onRenamed={(newTitle) => {
                handleRenameConversation(renameTarget.id, newTitle)
                if (hostedThreadId) {
                  registry?.updateTabTitle(hostedThreadId, newTitle)
                }
              }}
            />
          )}

          {/* Conversation History Panel */}
          <ConversationHistoryPanel
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
            onSelectConversation={handleSelectConversationWithReset}
            onArchiveConversation={handleArchiveConversation}
            onRenameConversation={handleRenameConversation}
            onNewChat={handleNewChatWithSkillReset}
            activeConversations={activeConversations}
            currentConversationId={currentConversationId || undefined}
            refreshTrigger={historyRefreshKey}
          />

          {documentReview && documentReviewHost
            ? createPortal(
                <DocumentEditReview
                  title={documentReview.title || 'Note'}
                  original={documentReview.original}
                  proposed={documentReview.proposed}
                  busy={isApplyingEdit}
                  scrollNonce={documentReviewScroll}
                  onDecide={handleDocumentReviewDecide}
                  onClose={() => setDocumentReview(null)}
                />,
                documentReviewHost,
              )
            : null}

          {/* Diff Preview Dialog */}
          {proposedEdit && (
            <DiffPreviewDialog
              open={diffPreviewOpen}
              onOpenChange={(open) => {
                setDiffPreviewOpen(open)
                if (!open) {
                  setBatchEditReview(null)
                  setProposedEdit(null)
                }
              }}
              original={proposedEdit.original}
              proposed={proposedEdit.proposed}
              filename={proposedEdit.filename}
              batchIndex={batchEditReview?.index}
              batchTotal={batchEditReview?.edits.length}
              onBatchPrevious={
                batchEditReview && batchEditReview.edits.length > 1
                  ? () => advanceBatchEditReview(batchEditReview.index, -1)
                  : undefined
              }
              onBatchNext={
                batchEditReview && batchEditReview.edits.length > 1
                  ? () => advanceBatchEditReview(batchEditReview.index, 1)
                  : undefined
              }
              onAccept={() => {
                devLog('[ChatPane] DiffPreviewDialog onAccept called, proposedEdit:', proposedEdit)
                if (!proposedEdit?.documentId) {
                  console.error('[ChatPane] proposedEdit missing documentId:', proposedEdit)
                  toast.error('Cannot apply changes', {
                    description: 'Document ID is missing from proposed edit'
                  })
                  return
                }
                void applyBatchEditDecision('accept', proposedEdit)
              }}
              onReject={() => {
                devLog('[ChatPane] DiffPreviewDialog onReject called, proposedEdit:', proposedEdit)
                if (!proposedEdit?.documentId) {
                  console.error('[ChatPane] proposedEdit missing documentId:', proposedEdit)
                  toast.error('Cannot reject changes', {
                    description: 'Document ID is missing from proposed edit'
                  })
                  return
                }
                void applyBatchEditDecision('reject', proposedEdit)
              }}
              onEdit={handleEditBeforeApply}
              isApplying={isApplyingEdit}
            />
          )}

          <FeedbackModal
            open={feedbackModalOpen}
            onOpenChange={setFeedbackModalOpen}
            initialDetail={feedbackModalDetail}
            messages={feedbackMessageSnapshot}
            conversationTitle={feedbackConversationTitle}
            workspaceScope={workspaceScopePaths}
            onSuccess={handleFeedbackFiled}
          />
        </>
      )}
    </div>
  )
})
